/**
 * Central data-testid registry — the SINGLE SOURCE OF TRUTH for all test ids.
 *
 * Format: {page}.{section}.{element-type}-{element-name}
 *   page:         route/module name (login, new-leads, plan-listing, agents, crm, ...)
 *   section:      form | table | filters | header | nav | dialog | tabs | list | ...
 *   element-type: btn | input | select | link | tab | toggle | checkbox | radio |
 *                 switch | menu | dialog | combobox | error | badge | row | readonly
 *
 * Rules:
 * 1. Values are kebab-case, dot-separated. Object keys are camelCase.
 * 2. Every interactive element MUST have a data-testid.
 * 3. Non-interactive elements get one only when automation asserts on their content.
 * 4. Reusable components take a `testIdPrefix` prop and use the generator
 *    functions below (testIds.filters.*, testIds.pagination.*).
 * 5. Dynamic ids (rows, per-item actions) use function generators.
 * 6. NEVER hardcode a data-testid string in JSX — import from this file.
 *    (Enforced by the no-restricted-syntax ESLint rule.)
 *
 * How to add ids: find (or add) the page object below, add camelCase keys,
 * follow the format, then run `pnpm validate:testids` — uniqueness and format
 * are checked by scripts/validate-testids.ts.
 */

export const testIds = {
  login: {
    form: {
      phone: 'login.form.input-phone',
      email: 'login.form.input-email',
      submit: 'login.form.btn-submit',
      toggleMode: 'login.form.btn-toggle-mode',
      usernameError: 'login.form.error-username',
    },
  },

  otp: {
    form: {
      code: (index: number) => `otp.form.input-code-${index}` as const,
      submit: 'otp.form.btn-submit',
      resend: 'otp.form.btn-resend',
      resetViaEmail: 'otp.form.btn-reset-via-email',
      error: 'otp.form.error-code',
    },
  },

  pinVerification: {
    form: {
      pin: 'pin-verification.form.input-pin',
      togglePin: 'pin-verification.form.btn-toggle-pin',
      error: 'pin-verification.form.error-pin',
      submit: 'pin-verification.form.btn-submit',
      forgot: 'pin-verification.form.link-forgot-pin',
      back: 'pin-verification.form.btn-back',
    },
  },

  pinCreation: {
    form: {
      pin: 'pin-creation.form.input-pin',
      togglePin: 'pin-creation.form.btn-toggle-pin',
      pinError: 'pin-creation.form.error-pin',
      confirmPin: 'pin-creation.form.input-confirm-pin',
      toggleConfirmPin: 'pin-creation.form.btn-toggle-confirm-pin',
      confirmPinError: 'pin-creation.form.error-confirm-pin',
      submit: 'pin-creation.form.btn-submit',
    },
  },

  resetPassword: {
    form: {
      password: 'reset-password.form.input-password',
      togglePassword: 'reset-password.form.btn-toggle-password',
      passwordError: 'reset-password.form.error-password',
      confirmPassword: 'reset-password.form.input-confirm-password',
      toggleConfirmPassword: 'reset-password.form.btn-toggle-confirm-password',
      confirmPasswordError: 'reset-password.form.error-confirm-password',
      submit: 'reset-password.form.btn-submit',
    },
  },

  affiliateSelect: {
    list: {
      item: (id: number | string) => `affiliate-select.list.row-${id}` as const,
      confirm: 'affiliate-select.list.btn-confirm',
      line: 'affiliate-select.list.link-line',
    },
  },

  // ---- leads module (Wave 2) ----
  leads: {
    tabs: { tab: (key: string) => `leads.tabs.tab-${key}` as const },
  },

  newLeads: {
    filters: {
      allFilters: 'new-leads.filters.btn-all-filters',
      cancel: 'new-leads.filters.btn-cancel',
      clear: 'new-leads.filters.btn-clear',
      apply: 'new-leads.filters.btn-apply',
      clearAll: 'new-leads.filters.btn-clear-all',
      download: 'new-leads.filters.btn-download',
    },
    table: {
      row: (id: number | string) => `new-leads.table.row-${id}` as const,
      expand: (id: number | string) => `new-leads.table.btn-expand-${id}` as const,
      menuTrigger: (id: number | string) => `new-leads.table.btn-menu-trigger-${id}` as const,
      action: (id: number | string, action: string) => `new-leads.table.btn-${action}-${id}` as const,
    },
    uploadDocument: {
      chooseFile: 'new-leads.upload-document.btn-choose-file',
      remove: 'new-leads.upload-document.btn-remove',
      cancel: 'new-leads.upload-document.btn-cancel',
      submit: 'new-leads.upload-document.btn-submit',
    },
    sendEmailToInsurer: {
      edit: 'new-leads.send-email-to-insurer.btn-edit',
      send: 'new-leads.send-email-to-insurer.btn-send',
    },
  },

  renewals: {
    filters: {
      allFilters: 'renewals.filters.btn-all-filters',
      hideRenewed: 'renewals.filters.toggle-hide-renewed',
      cancel: 'renewals.filters.btn-cancel',
      clear: 'renewals.filters.btn-clear',
      apply: 'renewals.filters.btn-apply',
      clearAll: 'renewals.filters.btn-clear-all',
    },
    table: {
      row: (id: number | string) => `renewals.table.row-${id}` as const,
      expand: (id: number | string) => `renewals.table.btn-expand-${id}` as const,
      menuTrigger: (id: number | string) => `renewals.table.btn-menu-trigger-${id}` as const,
      action: (id: number | string, action: string) => `renewals.table.btn-${action}-${id}` as const,
    },
    statusColumn: {
      select: (id: number | string) => `renewals.status-column.select-status-${id}` as const,
      confirmNo: 'renewals.status-column.btn-confirm-no',
      confirmYes: 'renewals.status-column.btn-confirm-yes',
    },
    uploadNotice: {
      fileInput: 'renewals.upload-notice.input-file',
      remove: 'renewals.upload-notice.btn-remove',
      cancel: 'renewals.upload-notice.btn-cancel',
      submit: 'renewals.upload-notice.btn-submit',
    },
    expandedRow: {
      claimStatus: 'renewals.expanded-row.select-claim-status',
      preview: (id: number | string) => `renewals.expanded-row.btn-preview-${id}` as const,
      delete: (id: number | string) => `renewals.expanded-row.btn-delete-${id}` as const,
    },
  },

  changeOfAgent: {
    filters: {
      allFilters: 'change-of-agent.filters.btn-all-filters',
      download: 'change-of-agent.filters.btn-download',
      cancel: 'change-of-agent.filters.btn-cancel',
      clear: 'change-of-agent.filters.btn-clear',
      apply: 'change-of-agent.filters.btn-apply',
      clearAll: 'change-of-agent.filters.btn-clear-all',
    },
    table: {
      row: (id: number | string) => `change-of-agent.table.row-${id}` as const,
      expand: (id: number | string) => `change-of-agent.table.btn-expand-${id}` as const,
      menuTrigger: (id: number | string) => `change-of-agent.table.btn-menu-trigger-${id}` as const,
      action: (id: number | string, action: string) => `change-of-agent.table.btn-${action}-${id}` as const,
    },
    expandedRow: {
      rejectRemark: 'change-of-agent.expanded-row.input-remark',
      rejectCancel: 'change-of-agent.expanded-row.btn-cancel',
      rejectSave: 'change-of-agent.expanded-row.btn-save',
      insurerStatus: 'change-of-agent.expanded-row.select-insurer-status',
      document: (id: number | string) => `change-of-agent.expanded-row.btn-document-${id}` as const,
    },
  },

  compulsoryInsurance: {
    filters: {
      fetchAll: 'compulsory-insurance.filters.toggle-fetch-all',
      fetchPolicyPdf: 'compulsory-insurance.filters.btn-fetch-policy-pdf',
      download: 'compulsory-insurance.filters.btn-download',
    },
    table: {
      row: (id: number | string) => `compulsory-insurance.table.row-${id}` as const,
      expand: (id: number | string) => `compulsory-insurance.table.btn-expand-${id}` as const,
      menuTrigger: (id: number | string) => `compulsory-insurance.table.btn-menu-trigger-${id}` as const,
      action: (id: number | string, action: string) => `compulsory-insurance.table.btn-${action}-${id}` as const,
    },
    cancelDialog: {
      keep: 'compulsory-insurance.cancel-dialog.btn-keep',
      cancel: 'compulsory-insurance.cancel-dialog.btn-cancel',
    },
    forceCancelDialog: {
      cancel: 'compulsory-insurance.force-cancel-dialog.btn-cancel',
      confirm: 'compulsory-insurance.force-cancel-dialog.btn-confirm',
    },
    addDetailsDialog: {
      policyNumber: 'compulsory-insurance.add-details-dialog.input-policy-number',
      premium: 'compulsory-insurance.add-details-dialog.input-premium',
      tax: 'compulsory-insurance.add-details-dialog.input-tax',
      duty: 'compulsory-insurance.add-details-dialog.input-duty',
      cancel: 'compulsory-insurance.add-details-dialog.btn-cancel',
      confirm: 'compulsory-insurance.add-details-dialog.btn-confirm',
    },
    expandedRow: {
      info: 'compulsory-insurance.expanded-row.btn-info',
      viewPolicyPdf: 'compulsory-insurance.expanded-row.btn-view-policy-pdf',
      document: (id: number | string) => `compulsory-insurance.expanded-row.btn-document-${id}` as const,
    },
  },

  endorsements: {
    // The filter panel on this base carries more controls than the original registry saw.
    filters: {
      showApproved: 'endorsements.filters.toggle-show-approved',
      allFilters: 'endorsements.filters.btn-all-filters',
      cancel: 'endorsements.filters.btn-cancel',
      clear: 'endorsements.filters.btn-clear',
      apply: 'endorsements.filters.btn-apply',
      clearAll: 'endorsements.filters.btn-clear-all',
      // Endorsement-type tree: a parent, its sub-options and a standalone cancellation box.
      typeOption: (key: string) => `endorsements.filters.checkbox-type-${key}` as const,
      removeChip: (key: string) => `endorsements.filters.btn-remove-${key}-chip` as const,
    },
    table: {
      row: (id: number | string) => `endorsements.table.row-${id}` as const,
      expand: (id: number | string) => `endorsements.table.btn-expand-${id}` as const,
      menuTrigger: (id: number | string) => `endorsements.table.btn-menu-trigger-${id}` as const,
      action: (id: number | string, action: string) => `endorsements.table.btn-${action}-${id}` as const,
    },
    approveDialog: {
      cancel: 'endorsements.approve-dialog.btn-cancel',
      confirm: 'endorsements.approve-dialog.btn-confirm',
    },
    cancelDialog: {
      cancel: 'endorsements.cancel-dialog.btn-cancel',
      confirm: 'endorsements.cancel-dialog.btn-confirm',
    },
    // Endorsement detail pages and their modals — added on this base, not in the original registry.
    details: {
      reasonModal: {
        reason: 'endorsements.details.reason-modal.input-reason',
        cancel: 'endorsements.details.reason-modal.btn-cancel',
        confirm: 'endorsements.details.reason-modal.btn-confirm',
      },
      uploadModal: {
        cancel: 'endorsements.details.upload-modal.btn-cancel',
        submit: 'endorsements.details.upload-modal.btn-submit',
      },
      trackingModal: {
        field: (name: string) => `endorsements.details.tracking-modal.input-${name}` as const,
        cancel: 'endorsements.details.tracking-modal.btn-cancel',
        save: 'endorsements.details.tracking-modal.btn-save',
      },
      premiumComparison: {
        info: (section: string) => `endorsements.details.premium-comparison.btn-info-${section}` as const,
        viewPassbook: 'endorsements.details.premium-comparison.btn-view-passbook',
      },
      page: {
        action: (action: string) => `endorsements.details.btn-${action}` as const,
        field: (name: string) => `endorsements.details.input-${name}` as const,
        select: (name: string) => `endorsements.details.select-${name}` as const,
        toggle: (name: string) => `endorsements.details.toggle-${name}` as const,
        document: (id: number | string) => `endorsements.details.btn-document-${id}` as const,
        // Inline explanatory lines on a detail card, e.g. note('refund-basis').
        note: (name: string) => `endorsements.details.note-${name}` as const,
        statusBadge: (status: string) => `endorsements.details.badge-status-${status}` as const,
        policyBadge: (policyType: string, state: string) =>
          `endorsements.details.badge-${state}-${policyType}` as const,
      },
      timeline: {
        // One toggle per reason chip in the progress timeline; scope is 'vmi' | 'cmi' | 'policy'.
        reasonToggle: (scope: string, idx: number) =>
          `endorsements.details.timeline.btn-reason-${scope}-${idx}` as const,
      },
      editResubmit: {
        action: (action: string) => `endorsements.details.edit-resubmit.btn-${action}` as const,
        field: (name: string) => `endorsements.details.edit-resubmit.input-${name}` as const,
        select: (name: string) => `endorsements.details.edit-resubmit.select-${name}` as const,
      },
    },
  },

  policyInProgress: {
    tabs: { tab: (key: string) => `policy-in-progress.tabs.tab-${key}` as const },
    informationCorrection: {
      field: (name: string) => `policy-in-progress.information-correction.input-${name}` as const,
      select: (name: string) => `policy-in-progress.information-correction.select-${name}` as const,
      date: (name: string) => `policy-in-progress.information-correction.date-${name}` as const,
      radio: (group: string, value: string) =>
        `policy-in-progress.information-correction.radio-${group}-${value}` as const,
      toggle: (name: string) => `policy-in-progress.information-correction.toggle-${name}` as const,
      action: (name: string) => `policy-in-progress.information-correction.btn-${name}` as const,
      driverField: (index: number, name: string) =>
        `policy-in-progress.information-correction.input-driver-${index}-${name}` as const,
      driverDate: (index: number, name: string) =>
        `policy-in-progress.information-correction.date-driver-${index}-${name}` as const,
      removeDriver: (index: number) => `policy-in-progress.information-correction.btn-remove-driver-${index}` as const,
    },
    vehicleDetails: {
      field: (name: string) => `policy-in-progress.vehicle-details.input-${name}` as const,
      select: (name: string) => `policy-in-progress.vehicle-details.select-${name}` as const,
      toggle: (name: string) => `policy-in-progress.vehicle-details.toggle-${name}` as const,
      date: (name: string) => `policy-in-progress.vehicle-details.date-${name}` as const,
      action: (name: string) => `policy-in-progress.vehicle-details.btn-${name}` as const,
    },
    reviewChanges: {
      action: (name: string) => `policy-in-progress.review-changes.btn-${name}` as const,
    },
    cancellationModal: {
      field: (name: string) => `policy-in-progress.cancellation-modal.input-${name}` as const,
      select: (name: string) => `policy-in-progress.cancellation-modal.select-${name}` as const,
      date: (name: string) => `policy-in-progress.cancellation-modal.date-${name}` as const,
      action: (name: string) => `policy-in-progress.cancellation-modal.btn-${name}` as const,
    },
    cancellationEndorsement: {
      field: (name: string) => `policy-in-progress.cancellation-endorsement.input-${name}` as const,
      select: (name: string) => `policy-in-progress.cancellation-endorsement.select-${name}` as const,
      date: (name: string) => `policy-in-progress.cancellation-endorsement.date-${name}` as const,
      action: (name: string) => `policy-in-progress.cancellation-endorsement.btn-${name}` as const,
      document: (key: string) => `policy-in-progress.cancellation-endorsement.btn-document-${key}` as const,
      // Yes/No rows rendered with InlineRadio — e.g. radio('has-claim', 'yes').
      radio: (group: string, value: string) =>
        `policy-in-progress.cancellation-endorsement.radio-${group}-${value}` as const,
      // Inline explanatory lines, e.g. note('refund-basis') for the insurer-derived
      // refund-basis sentence. Kebab-case names only — the registry format check rejects
      // underscores.
      note: (name: string) => `policy-in-progress.cancellation-endorsement.note-${name}` as const,
    },
    recordEndorsement: {
      field: (name: string) => `policy-in-progress.record-endorsement.input-${name}` as const,
      select: (name: string) => `policy-in-progress.record-endorsement.select-${name}` as const,
      date: (name: string) => `policy-in-progress.record-endorsement.date-${name}` as const,
      action: (name: string) => `policy-in-progress.record-endorsement.btn-${name}` as const,
      document: (key: string) => `policy-in-progress.record-endorsement.btn-document-${key}` as const,
    },
    premiumUpdate: {
      field: (name: string) => `policy-in-progress.premium-update.input-${name}` as const,
      select: (name: string) => `policy-in-progress.premium-update.select-${name}` as const,
      action: (name: string) => `policy-in-progress.premium-update.btn-${name}` as const,
    },
    refundDetails: {
      field: (name: string) => `policy-in-progress.refund-details.input-${name}` as const,
      select: (name: string) => `policy-in-progress.refund-details.select-${name}` as const,
      action: (name: string) => `policy-in-progress.refund-details.btn-${name}` as const,
    },
    filters: {
      download: 'policy-in-progress.filters.btn-download',
      allFilters: 'policy-in-progress.filters.btn-all-filters',
      cancel: 'policy-in-progress.filters.btn-cancel',
      clear: 'policy-in-progress.filters.btn-clear',
      apply: 'policy-in-progress.filters.btn-apply',
      clearAll: 'policy-in-progress.filters.btn-clear-all',
    },
    table: {
      row: (id: number | string) => `policy-in-progress.table.row-${id}` as const,
      expand: (id: number | string) => `policy-in-progress.table.btn-expand-${id}` as const,
      menuTrigger: (id: number | string) => `policy-in-progress.table.btn-menu-trigger-${id}` as const,
      action: (id: number | string, action: string) => `policy-in-progress.table.btn-${action}-${id}` as const,
    },
    expandedRow: {
      document: (key: string) => `policy-in-progress.expanded-row.btn-document-${key}` as const,
      remarkInput: (id: number | string) => `policy-in-progress.expanded-row.input-remark-${id}` as const,
      remarkSave: (id: number | string) => `policy-in-progress.expanded-row.btn-save-${id}` as const,
      remarkCancel: (id: number | string) => `policy-in-progress.expanded-row.btn-cancel-${id}` as const,
      remarkEdit: (id: number | string) => `policy-in-progress.expanded-row.btn-edit-${id}` as const,
      remarkDelete: (id: number | string) => `policy-in-progress.expanded-row.btn-delete-${id}` as const,
    },
    docsRejectionDialog: {
      cancel: 'policy-in-progress.docs-rejection-dialog.btn-cancel',
      confirm: 'policy-in-progress.docs-rejection-dialog.btn-confirm',
    },
    policyModal: {
      tab: (key: string) => `policy-in-progress.policy-modal.tab-${key}` as const,
      cancel: 'policy-in-progress.policy-modal.btn-cancel',
      submit: 'policy-in-progress.policy-modal.btn-submit',
      field: (type: string, name: string) => `policy-in-progress.policy-modal.input-${name}-${type}` as const,
      select: (type: string, name: string) => `policy-in-progress.policy-modal.select-${name}-${type}` as const,
      radio: (type: string, name: string) => `policy-in-progress.policy-modal.radio-${name}-${type}` as const,
      toggle: (type: string, name: string) => `policy-in-progress.policy-modal.toggle-${name}-${type}` as const,
      docAddFile: (type: string) => `policy-in-progress.policy-modal.btn-add-file-${type}` as const,
      docPreviewFile: (id: number | string) => `policy-in-progress.policy-modal.btn-preview-file-${id}` as const,
      docRemoveFile: (type: string, index: number) =>
        `policy-in-progress.policy-modal.btn-remove-file-${type}-${index}` as const,
    },
  },

  // Cross-feature shared components rendered from multiple leads pages (dialogs/columns under
  // src/modules/leads/shared/**). Only one instance renders at a time, so fixed ids are safe.
  leadsShared: {
    addRemarks: {
      remark: 'leads-shared.add-remarks.input-remark',
      cancel: 'leads-shared.add-remarks.btn-cancel',
      submit: 'leads-shared.add-remarks.btn-submit',
    },
    changeAgent: {
      agent: 'leads-shared.change-agent.select-agent',
      cancel: 'leads-shared.change-agent.btn-cancel',
      submit: 'leads-shared.change-agent.btn-submit',
    },
    changeRf: {
      rf: 'leads-shared.change-rf.select-rf',
      reason: 'leads-shared.change-rf.select-reason',
      cancel: 'leads-shared.change-rf.btn-cancel',
      submit: 'leads-shared.change-rf.btn-submit',
    },
    updateSc: {
      sc: 'leads-shared.update-sc.select-sc',
      cancel: 'leads-shared.update-sc.btn-cancel',
      submit: 'leads-shared.update-sc.btn-submit',
    },
    recordEndorsement: {
      close: 'leads-shared.record-endorsement.btn-close',
    },
    rfColumn: {
      transfer: (id: number | string) => `leads-shared.rf-column.btn-transfer-${id}` as const,
    },
    scColumn: {
      claim: (id: number | string) => `leads-shared.sc-column.btn-claim-${id}` as const,
    },
    showColumn: {
      toggle: (id: number | string) => `leads-shared.show-column.toggle-${id}` as const,
    },
    statusColumn: {
      select: (id: number | string) => `leads-shared.status-column.select-status-${id}` as const,
      remark: 'leads-shared.status-column.input-remark',
      cancel: 'leads-shared.status-column.btn-cancel',
      save: 'leads-shared.status-column.btn-save',
    },
    paymentStatusColumn: {
      select: (id: number | string) => `leads-shared.payment-status-column.select-status-${id}` as const,
    },
    policyStatusColumn: {
      select: (id: number | string) => `leads-shared.policy-status-column.select-status-${id}` as const,
    },
    vehicleColumn: {
      trigger: (id: number | string) => `leads-shared.vehicle-column.btn-${id}` as const,
    },
    editRemark: {
      remark: 'leads-shared.edit-remark.input-remark',
      cancel: 'leads-shared.edit-remark.btn-cancel',
      submit: 'leads-shared.edit-remark.btn-submit',
    },
    remarksSection: {
      edit: (id: number | string) => `leads-shared.remarks-section.btn-edit-${id}` as const,
      delete: (id: number | string) => `leads-shared.remarks-section.btn-delete-${id}` as const,
    },
    insurerTable: {
      status: (id: number | string) => `leads-shared.insurer-table.select-status-${id}` as const,
      moreTrigger: (id: number | string) => `leads-shared.insurer-table.btn-more-${id}` as const,
      moreItem: (id: number | string, action: string) => `leads-shared.insurer-table.btn-${action}-${id}` as const,
    },
    documentViewer: {
      prev: 'leads-shared.document-viewer.btn-prev',
      next: 'leads-shared.document-viewer.btn-next',
      share: 'leads-shared.document-viewer.btn-share',
      download: 'leads-shared.document-viewer.btn-download',
      zoomIn: 'leads-shared.document-viewer.btn-zoom-in',
      zoomOut: 'leads-shared.document-viewer.btn-zoom-out',
      reset: 'leads-shared.document-viewer.btn-reset',
      downloadFallback: 'leads-shared.document-viewer.btn-download-fallback',
    },
    pricelist: {
      cancel: 'leads-shared.pricelist.btn-cancel',
      submit: 'leads-shared.pricelist.btn-submit',
      insurer: 'leads-shared.pricelist.select-insurer',
      insurerReadonly: 'leads-shared.pricelist.input-insurer-readonly',
      carTypeReadonly: 'leads-shared.pricelist.readonly-car-type',
      vehicleCodeReadonly: 'leads-shared.pricelist.readonly-vehicle-code',
      fileInput: 'leads-shared.pricelist.input-file',
      ocrRetry: 'leads-shared.pricelist.btn-ocr-retry',
      replace: 'leads-shared.pricelist.btn-replace-file',
      deleteFile: 'leads-shared.pricelist.btn-delete-file',
      revertDeletion: 'leads-shared.pricelist.btn-revert-deletion',
      upload: 'leads-shared.pricelist.btn-upload',
      priceId: 'leads-shared.pricelist.input-price-id',
      fetch: 'leads-shared.pricelist.btn-fetch',
      ocrApply: 'leads-shared.pricelist.btn-ocr-apply',
      ocrCancel: 'leads-shared.pricelist.btn-ocr-cancel',
      planName: 'leads-shared.pricelist.input-plan-name',
      garageType: 'leads-shared.pricelist.radio-garage-type',
      insuranceClass: (name: string) => `leads-shared.pricelist.radio-insurance-class-${name}` as const,
      premiumExpiryDate: 'leads-shared.pricelist.input-premium-expiry-date',
      claimsHistory: 'leads-shared.pricelist.radio-claims-history',
      field: (name: string) => `leads-shared.pricelist.input-${name}` as const,
      checkbox: (name: string) => `leads-shared.pricelist.checkbox-${name}` as const,
      autoCalculate: 'leads-shared.pricelist.toggle-auto-calculate',
      // Grouped to match `DocumentPreviewTestIds` so the call site can pass this object straight
      // through. The id strings are unchanged.
      preview: {
        prev: 'leads-shared.pricelist.btn-preview-prev',
        next: 'leads-shared.pricelist.btn-preview-next',
        zoomIn: 'leads-shared.pricelist.btn-preview-zoom-in',
        zoomOut: 'leads-shared.pricelist.btn-preview-zoom-out',
        reset: 'leads-shared.pricelist.btn-preview-reset',
      },
    },
  },

  // ---- comparison-sheet module (Wave 3) ----
  comparisonSheet: {
    header: {
      sendToAgent: 'comparison-sheet.header.btn-send-to-agent',
      sendToAgentWithCommission: 'comparison-sheet.header.btn-send-to-agent-commission',
      downloadQuotations: 'comparison-sheet.header.btn-download-quotations',
      downloadWithCommission: 'comparison-sheet.header.btn-download-with-commission',
    },
    paymentTerms: {
      option: (id: string) => `comparison-sheet.payment-terms.radio-option-${id}` as const,
      tenureAll: 'comparison-sheet.payment-terms.radio-tenure-all',
      tenure: (key: string) => `comparison-sheet.payment-terms.radio-tenure-${key}` as const,
    },
    imgView: {
      close: 'comparison-sheet.img-view.btn-close',
    },
    priceCard: {
      reportSale: (id: number | string) => `comparison-sheet.price-card.btn-report-sale-${id}` as const,
    },
    carousel: {
      prev: 'comparison-sheet.carousel.btn-prev',
      next: 'comparison-sheet.carousel.btn-next',
    },
  },

  // ---- create-leads module (Wave 3) ----
  legacyWizardRedirect: {
    page: {
      resolving: 'legacy-wizard-redirect.page.readonly-resolving',
    },
    notFoundDialog: {
      dialog: 'legacy-wizard-redirect.not-found-dialog.dialog',
      backToCreateLeads: 'legacy-wizard-redirect.not-found-dialog.btn-back-to-create-leads',
    },
  },
  createLeads: {
    affiliateSelectionModal: {
      searchAgent: 'create-leads.affiliate-selection-modal.combobox-search-agent',
      cancel: 'create-leads.affiliate-selection-modal.btn-cancel',
      confirm: 'create-leads.affiliate-selection-modal.btn-confirm',
    },
    leadgenCodeModal: {
      code: 'create-leads.leadgen-code-modal.input-code',
      cancel: 'create-leads.leadgen-code-modal.btn-cancel',
      submit: 'create-leads.leadgen-code-modal.btn-submit',
    },
    buyInsuranceContent: {
      staticProduct: (key: string) => `create-leads.buy-insurance-content.btn-product-${key}` as const,
      nonMotorProduct: (id: number | string) =>
        `create-leads.buy-insurance-content.btn-non-motor-product-${id}` as const,
    },
  },

  // ---- prequote-forms/CustomQuote module (Wave 3) ----
  prequoteCustomQuote: {
    // Bike variant of the custom-quote form (src/modules/prequote-forms/CustomQuote/Bike).
    bike: {
      note: 'prequote-custom-quote.bike.input-note',
      submit: 'prequote-custom-quote.bike.btn-submit',
    },
    form: {
      btnCancel: 'prequote-custom-quote.form.btn-cancel',
      btnReviewRequest: 'prequote-custom-quote.form.btn-review-request',
      btnRetry: 'prequote-custom-quote.form.btn-retry',
    },
    registrationDetails: {
      radioLicenseType: (value: string) =>
        `prequote-custom-quote.registration-details.radio-license-type-${value.replace(/_/g, '-')}` as const,
      inputVehicleNumber: 'prequote-custom-quote.registration-details.input-vehicle-number',
    },
    insuranceDetails: {
      radioDriverType: (value: string) =>
        `prequote-custom-quote.insurance-details.radio-driver-type-${value.replace(/_/g, '-')}` as const,
      inputDriverName: (num: 1 | 2) => `prequote-custom-quote.insurance-details.input-driver-${num}-name` as const,
      datepickerDriverBirthday: (num: 1 | 2) =>
        `prequote-custom-quote.insurance-details.datepicker-driver-${num}-birthday` as const,
    },
    insurerSelection: {
      radioInsuranceClass: (index: number, classId: string) =>
        `prequote-custom-quote.insurer-selection.radio-insurance-class-${index}-${classId.replace(/_/g, '-')}` as const,
      radioGarageType: (index: number, garageId: string) =>
        `prequote-custom-quote.insurer-selection.radio-garage-type-${index}-${garageId.replace(/_/g, '-')}` as const,
      btnAddInsurer: 'prequote-custom-quote.insurer-selection.btn-add-insurer',
      btnRemoveInsurer: (index: number) =>
        `prequote-custom-quote.insurer-selection.btn-remove-insurer-${index}` as const,
    },
    documentUploader: {
      btnAdd: (prefix: string) => `${prefix}.btn-add` as const,
      inputFile: (prefix: string) => `${prefix}.input-file` as const,
      btnViewFile: (prefix: string, index: number) => `${prefix}.btn-view-${index}` as const,
      btnRemoveFile: (prefix: string, index: number) => `${prefix}.btn-remove-${index}` as const,
    },
    additionalInfo: {
      textareaAdditionalText: 'prequote-custom-quote.additional-info.input-additional-text',
    },
    reviewStage: {
      btnBack: 'prequote-custom-quote.review-stage.btn-back',
      btnSubmit: 'prequote-custom-quote.review-stage.btn-submit',
    },
    successModal: {
      btnBackToPlans: 'prequote-custom-quote.success-modal.btn-back-to-plans',
      btnGoToLeads: 'prequote-custom-quote.success-modal.btn-go-to-leads',
    },
    duplicateModal: {
      btnCreateNewLead: 'prequote-custom-quote.duplicate-modal.btn-create-new-lead',
      btnViewExisting: 'prequote-custom-quote.duplicate-modal.btn-view-existing-lead',
    },
  },

  // ---- prequote-forms/VMI module (Wave 3) ----
  prequoteVmi: {
    form: {
      cancel: 'prequote-vmi.form.btn-cancel',
      submit: 'prequote-vmi.form.btn-submit',
    },
    registrationDetails: {
      vehicleNumber: 'prequote-vmi.registration-details.input-vehicle-number',
    },
    insuranceDetails: {
      commercialUse: 'prequote-vmi.insurance-details.checkbox-commercial-use',
      accessories: 'prequote-vmi.insurance-details.checkbox-accessories',
      sumInsuredInfo: 'prequote-vmi.insurance-details.btn-sum-insured-info',
      incrementDecrement: {
        decrement: (name: string) => `prequote-vmi.insurance-details.btn-decrement-${name}` as const,
        input: (name: string) => `prequote-vmi.insurance-details.input-${name}` as const,
        increment: (name: string) => `prequote-vmi.insurance-details.btn-increment-${name}` as const,
        reset: (name: string) => `prequote-vmi.insurance-details.btn-reset-${name}` as const,
      },
    },
    additionalInfo: {
      chatwootLink: 'prequote-vmi.additional-info.input-chatwoot-link',
      remarks: 'prequote-vmi.additional-info.input-remarks',
    },
    duplicateModal: {
      createNewLead: 'prequote-vmi.duplicate-modal.btn-create-new-lead',
      viewExistingLead: 'prequote-vmi.duplicate-modal.btn-view-existing-lead',
    },
    sumInsuredReasonModal: {
      otherReason: 'prequote-vmi.sum-insured-reason-modal.input-other-reason',
      submit: 'prequote-vmi.sum-insured-reason-modal.btn-submit',
    },
  },

  // ---- prequote-forms/COA module (Wave 3) ----
  prequoteCoa: {
    form: {
      cancel: 'prequote-coa.form.btn-cancel',
      submit: 'prequote-coa.form.btn-submit',
    },
    confirmationModal: {
      cancel: 'prequote-coa.confirmation-modal.btn-cancel',
      confirm: 'prequote-coa.confirmation-modal.btn-confirm',
    },
    documents: {
      insuredTypeIndividual: 'prequote-coa.documents.btn-insured-type-individual',
      insuredTypeCorporate: 'prequote-coa.documents.btn-insured-type-corporate',
      viewExample: (index: number) => `prequote-coa.documents.btn-view-example-${index}` as const,
    },
    additionalInfo: {
      chatwootLink: 'prequote-coa.additional-info.input-chatwoot-link',
      remarks: 'prequote-coa.additional-info.input-remarks',
    },
    duplicateModal: {
      createNewLead: 'prequote-coa.duplicate-modal.btn-create-new-lead',
      viewExistingLead: 'prequote-coa.duplicate-modal.btn-view-existing-lead',
    },
    previousInsuranceDetails: {
      policyExpiryDate: 'prequote-coa.previous-insurance-details.input-policy-expiry-date',
      vehicleNumber: 'prequote-coa.previous-insurance-details.input-vehicle-number',
    },
  },

  // ---- prequote-forms/NonMotor module (Wave 3) ----
  prequoteNonMotor: {
    form: {
      cancel: 'prequote-non-motor.form.btn-cancel',
      submit: 'prequote-non-motor.form.btn-submit',
    },
    infoModal: {
      action: 'prequote-non-motor.info-modal.btn-action',
      close: 'prequote-non-motor.info-modal.btn-close',
    },
  },

  // ---- plan-listing module (Wave 3) ----
  planListing: {
    page: {
      retry: 'plan-listing.page.btn-retry',
    },
    header: {
      edit: 'plan-listing.header.btn-edit',
      addCustomQuote: 'plan-listing.header.btn-add-custom-quote',
      requestCustomQuote: 'plan-listing.header.btn-request-custom-quote',
    },
    filters: {
      floodProtection: 'plan-listing.filters.checkbox-flood-protection',
    },
    filterChips: {
      remove: (key: string) => `plan-listing.filter-chips.btn-remove-${key}` as const,
      clearAll: 'plan-listing.filter-chips.btn-clear-all',
    },
    catalog: {
      provinceCombobox: 'plan-listing.catalog.combobox-province',
      retry: 'plan-listing.catalog.btn-retry',
      sortTrigger: 'plan-listing.catalog.btn-sort-trigger',
      sortItem: (value: string) => `plan-listing.catalog.menu-sort-${value}` as const,
      blockedAgentClose: 'plan-listing.catalog.btn-blocked-agent-close',
    },
    carousel: {
      previous: 'plan-listing.carousel.btn-previous',
      next: 'plan-listing.carousel.btn-next',
    },
    list: {
      card: (planId: number | string) => `plan-listing.list.row-${planId}` as const,
      checkbox: (planId: number | string) => `plan-listing.list.checkbox-select-${planId}` as const,
      copyId: (planId: number | string) => `plan-listing.list.btn-copy-id-${planId}` as const,
      terms: (planId: number | string) => `plan-listing.list.btn-terms-${planId}` as const,
      reportSale: (planId: number | string) => `plan-listing.list.btn-report-sale-${planId}` as const,
      toggleExpand: (planId: number | string) => `plan-listing.list.btn-toggle-expand-${planId}` as const,
      toggleComparison: (planId: number | string) => `plan-listing.list.btn-toggle-comparison-${planId}` as const,
    },
    expandedRow: {
      link: (type: string) => `plan-listing.expanded-row.link-${type}` as const,
    },
    noPlansFound: {
      addCustomQuote: 'plan-listing.no-plans-found.btn-add-custom-quote',
      requestCustomQuote: 'plan-listing.no-plans-found.btn-request-custom-quote',
      clearFilters: 'plan-listing.no-plans-found.btn-clear-filters',
    },
    editCarDetails: {
      cancel: 'plan-listing.edit-car-details.btn-cancel',
      save: 'plan-listing.edit-car-details.btn-save',
    },
    bottomBar: {
      clearSelections: 'plan-listing.bottom-bar.btn-clear-selections',
      sendToCustomer: 'plan-listing.bottom-bar.btn-send-to-customer',
      miniCardRemove: (planId: number | string) => `plan-listing.bottom-bar.btn-remove-${planId}` as const,
      miniCardReportSale: (planId: number | string) => `plan-listing.bottom-bar.btn-report-sale-${planId}` as const,
    },
    sumInsuredModal: {
      select: 'plan-listing.sum-insured-modal.select-sum-insured',
      cancel: 'plan-listing.sum-insured-modal.btn-cancel',
      confirm: 'plan-listing.sum-insured-modal.btn-confirm',
    },
    saleReporting: {
      cancel: 'plan-listing.sale-reporting.btn-cancel',
      continue: 'plan-listing.sale-reporting.btn-continue',
      reportSale: 'plan-listing.sale-reporting.btn-report-sale',
      unlicensedAgentWarning: {
        dialog: 'plan-listing.sale-reporting.dialog-unlicensed-agent-warning',
        confirm: 'plan-listing.sale-reporting.btn-unlicensed-agent-warning-confirm',
        cancel: 'plan-listing.sale-reporting.btn-unlicensed-agent-warning-cancel',
        expiredLicense: 'plan-listing.sale-reporting.text-unlicensed-agent-warning-expired-license',
        duplicateLicense: 'plan-listing.sale-reporting.text-unlicensed-agent-warning-duplicate-license',
      },
      sumInsuredInput: 'plan-listing.sale-reporting.input-sum-insured',
      accessories: {
        radioOption: (index: number) => `plan-listing.sale-reporting.radio-accessories-${index}` as const,
        itemName: (index: number) => `plan-listing.sale-reporting.input-accessory-name-${index}` as const,
        itemValue: (index: number) => `plan-listing.sale-reporting.input-accessory-value-${index}` as const,
        removeItem: (index: number) => `plan-listing.sale-reporting.btn-remove-accessory-${index}` as const,
        addItem: 'plan-listing.sale-reporting.btn-add-accessory',
      },
      // Switch-package flow — reached from Update Sale, not present in the original sale-reporting stage.
      switchPackage: {
        back: 'plan-listing.sale-reporting.btn-switch-package-back',
        continue: 'plan-listing.sale-reporting.btn-switch-package-continue',
        paymentOption: (value: string) => `plan-listing.sale-reporting.radio-payment-option-${value}` as const,
        addCmi: 'plan-listing.sale-reporting.checkbox-add-cmi',
        instalmentTenure: 'plan-listing.sale-reporting.select-instalment-tenure',
      },
      informationCorrection: {
        reviewNotifyInsurer: 'plan-listing.sale-reporting.checkbox-information-correction-notify-insurer',
        reviewBack: 'plan-listing.sale-reporting.btn-information-correction-back',
        reviewConfirm: 'plan-listing.sale-reporting.btn-information-correction-confirm',
      },
    },
    sendToCustomer: {
      back: 'plan-listing.send-to-customer.btn-back',
      cancel: 'plan-listing.send-to-customer.btn-cancel',
      continue: 'plan-listing.send-to-customer.btn-continue',
      customerInfo: {
        vehicleNumber: 'plan-listing.send-to-customer.input-vehicle-number',
        commercialUse: 'plan-listing.send-to-customer.checkbox-commercial-use',
        firstName: 'plan-listing.send-to-customer.input-first-name',
        lastName: 'plan-listing.send-to-customer.input-last-name',
        phoneNumber: 'plan-listing.send-to-customer.input-phone-number',
        discountAmount: 'plan-listing.send-to-customer.input-discount-amount',
        discountType: 'plan-listing.send-to-customer.select-discount-type',
        cameraDiscount: 'plan-listing.send-to-customer.checkbox-camera-discount',
        driverDiscount: 'plan-listing.send-to-customer.checkbox-driver-discount',
        driverAgeRange: 'plan-listing.send-to-customer.select-driver-age-range',
      },
    },
  },

  // ---- non-motor-plan-listing module (Wave 3) ----
  nonMotorPlanListing: {
    planCard: {
      brochure: (id: number | string) => `non-motor-plan-listing.plan-card.btn-brochure-${id}` as const,
      reportSale: (id: number | string) => `non-motor-plan-listing.plan-card.btn-report-sale-${id}` as const,
    },
    declaration: {
      yes: (code: string) => `non-motor-plan-listing.declaration.btn-yes-${code}` as const,
      no: (code: string) => `non-motor-plan-listing.declaration.btn-no-${code}` as const,
      cancel: 'non-motor-plan-listing.declaration.btn-cancel',
      continue: 'non-motor-plan-listing.declaration.btn-continue',
    },
    postquote: {
      cancel: 'non-motor-plan-listing.postquote.btn-cancel',
      continue: 'non-motor-plan-listing.postquote.btn-continue',
    },
    s3Uploader: {
      chooseFile: 'non-motor-plan-listing.s3-uploader.input-choose-file',
      remove: 'non-motor-plan-listing.s3-uploader.btn-remove',
      viewFile: 'non-motor-plan-listing.s3-uploader.link-view-file',
    },
    payment: {
      back: 'non-motor-plan-listing.payment.btn-back',
      cancel: 'non-motor-plan-listing.payment.btn-cancel',
      payNextInstalment: 'non-motor-plan-listing.payment.btn-pay-next-instalment',
      payRemaining: 'non-motor-plan-listing.payment.btn-pay-remaining',
      proceedToPay: 'non-motor-plan-listing.payment.btn-proceed-to-pay',
      topUp: 'non-motor-plan-listing.payment.btn-top-up',
      download: 'non-motor-plan-listing.payment.btn-download',
      refreshStatus: 'non-motor-plan-listing.payment.btn-refresh-status',
      goToPolicies: 'non-motor-plan-listing.payment.btn-go-to-policies',
    },
    selectorGroup: {
      select: (prefix: string, key: string) => `${prefix}.tab-${key}` as const,
    },
  },

  // ---- non-motor-details module (Wave 3) ----
  nonMotorDetails: {
    main: {
      editStatus: 'non-motor-details.main.btn-edit-status',
      completePayment: 'non-motor-details.main.btn-complete-payment',
      updateChanges: 'non-motor-details.main.btn-update-changes',
      uploadPolicy: (id: number | string) => `non-motor-details.main.btn-upload-policy-${id}` as const,
      tab: (key: string) => `non-motor-details.main.toggle-${key}` as const,
    },
    premium: {
      field: (name: string) => `non-motor-details.premium.input-${name}` as const,
    },
    policyInformation: {
      policyNumber: 'non-motor-details.policy-information.input-policy-number',
      trackingCode: 'non-motor-details.policy-information.input-tracking-code',
      copyOfPolicy: 'non-motor-details.policy-information.checkbox-copy-of-policy',
      policyStartDate: 'non-motor-details.policy-information.input-policy-start-date',
      policyEndDate: 'non-motor-details.policy-information.input-policy-end-date',
    },
    editStatus: {
      cancel: 'non-motor-details.edit-status.btn-cancel',
      update: 'non-motor-details.edit-status.btn-update',
    },
    uploadPolicySheet: {
      cancel: 'non-motor-details.upload-policy-sheet.btn-cancel',
      submit: 'non-motor-details.upload-policy-sheet.btn-submit',
    },
    commission: {
      field: 'non-motor-details.commission.input-agent-commission',
    },
  },

  // ---- agents module (Wave 4) ----
  // all-agents / mlm-agents / cmi-subagents / teams / downline
  agents: {
    table: {
      row: (id: number | string) => `agents.table.row-${id}` as const,
      action: (id: number | string, action: string) => `agents.table.btn-${action}-${id}` as const,
    },
  },
  mlmAgents: {
    table: {
      row: (id: number | string) => `mlm-agents.table.row-${id}` as const,
      action: (id: number | string, action: string) => `mlm-agents.table.btn-${action}-${id}` as const,
    },
  },
  cmiSubagents: {
    filters: {
      masterAgent: 'cmi-subagents.filters.select-master-agent',
    },
    table: {
      row: (id: number | string) => `cmi-subagents.table.row-${id}` as const,
      menuTrigger: (id: number | string) => `cmi-subagents.table.btn-menu-trigger-${id}` as const,
      action: (id: number | string, action: string) => `cmi-subagents.table.btn-${action}-${id}` as const,
    },
    topUpDialog: {
      amount: 'cmi-subagents.top-up-dialog.input-amount',
      cancel: 'cmi-subagents.top-up-dialog.btn-cancel',
      submit: 'cmi-subagents.top-up-dialog.btn-submit',
    },
  },
  cmiSubagentsCommissions: {
    filters: {
      insurerTrigger: 'cmi-subagents-commissions.filters.btn-insurer-trigger',
      insurerItem: (value: string) =>
        `cmi-subagents-commissions.filters.menu-insurer-${value.replace(/_/g, '-')}` as const,
      productTrigger: 'cmi-subagents-commissions.filters.btn-product-trigger',
      productItem: (value: string) =>
        `cmi-subagents-commissions.filters.menu-product-${value.replace(/_/g, '-')}` as const,
    },
    table: {
      rate: (index: number) => `cmi-subagents-commissions.table.input-rate-${index}` as const,
    },
    page: {
      cancel: 'cmi-subagents-commissions.page.btn-cancel',
      update: 'cmi-subagents-commissions.page.btn-update',
    },
  },
  teams: {
    header: {
      requestLevelUpgrade: 'teams.header.btn-request-level-upgrade',
      info: 'teams.header.btn-info',
    },
    dashboard: {
      infoTooltip: 'teams.dashboard.btn-info',
    },
    performance: {
      infoTooltip: 'teams.performance.btn-info',
      downloadTrigger: 'teams.performance.btn-download-trigger',
      downloadItem: (reportType: string) => `teams.performance.menu-download-${reportType.replace(/_/g, '-')}` as const,
    },
    // Generic generators shared by TeamsTable/ActionsCell, which render on both /teams and
    // /downline routes. Callers pass their own page.section prefix (e.g. "teams.table" or
    // "downline.table") so ids stay correctly namespaced per route.
    sharedTable: {
      row: (prefix: string, id: number | string) => `${prefix}.row-${id}` as const,
      menuTrigger: (prefix: string, id: number | string) => `${prefix}.btn-menu-trigger-${id}` as const,
      action: (prefix: string, id: number | string, action: string) => `${prefix}.btn-${action}-${id}` as const,
    },
    downloadReportModal: {
      email: 'teams.download-report-modal.input-email',
      cancel: 'teams.download-report-modal.btn-cancel',
      submit: 'teams.download-report-modal.btn-submit',
    },
    requestLevelUpgradeModal: {
      monthRangeTrigger: 'teams.request-level-upgrade-modal.btn-month-range-trigger',
      level: 'teams.request-level-upgrade-modal.combobox-level',
      terms: 'teams.request-level-upgrade-modal.checkbox-terms',
      cancel: 'teams.request-level-upgrade-modal.btn-cancel',
      submit: 'teams.request-level-upgrade-modal.btn-submit',
    },
  },
  downline: {
    breadcrumb: {
      ancestor: (id: number | string) => `downline.breadcrumb.btn-ancestor-${id}` as const,
      expand: 'downline.breadcrumb.btn-expand',
    },
  },

  // src/modules/agents/agent-detail/** (route /crm/affiliate/:id)
  crmAffiliate: {
    header: {
      viewSelector: 'crm-affiliate.header.btn-view-selector',
      menuItem: (key: string) => `crm-affiliate.header.menu-${key}` as const,
      historyBtn: 'crm-affiliate.header.btn-history',
    },
    // Generic pattern for EditableField.tsx, namespaced by fieldKey (unique across the page).
    editableField: {
      field: (name: string) => `crm-affiliate.editable-field.input-${name}` as const,
      cancel: (name: string) => `crm-affiliate.editable-field.btn-cancel-${name}` as const,
      confirm: (name: string) => `crm-affiliate.editable-field.btn-confirm-${name}` as const,
    },
    actions: {
      buyInsuranceTrigger: 'crm-affiliate.actions.btn-buy-insurance',
      buyInsuranceItem: (key: string) => `crm-affiliate.actions.menu-buy-insurance-${key}` as const,
      userActionsTrigger: 'crm-affiliate.actions.btn-user-actions',
      userActionsItem: (key: string) => `crm-affiliate.actions.menu-user-action-${key}` as const,
      addInvoiceTrigger: 'crm-affiliate.actions.btn-add-invoice',
      addInvoiceItem: (key: string) => `crm-affiliate.actions.menu-add-invoice-${key}` as const,
      downloadDocumentsTrigger: 'crm-affiliate.actions.btn-download-documents',
      downloadPeriodSelect: 'crm-affiliate.actions.select-download-period',
      downloadTypeRadio: (key: string) => `crm-affiliate.actions.radio-download-type-${key}` as const,
      downloadCancelBtn: 'crm-affiliate.actions.btn-download-cancel',
      downloadSubmitBtn: 'crm-affiliate.actions.btn-download-submit',
      blockedAgentClose: 'crm-affiliate.actions.btn-blocked-agent-close',
    },
    tags: {
      addTrigger: (list: string) => `crm-affiliate.tags.btn-add-${list}` as const,
      input: 'crm-affiliate.tags.input-tag-name',
      submit: 'crm-affiliate.tags.btn-submit-tag',
      suggestion: (name: string) => `crm-affiliate.tags.menu-suggestion-${name}` as const,
      chip: (name: string) => `crm-affiliate.tags.badge-${name}` as const,
      remove: (name: string) => `crm-affiliate.tags.btn-remove-${name}` as const,
    },
    creditSection: {
      refreshBtn: 'crm-affiliate.credit-section.btn-refresh',
      editMenuTrigger: 'crm-affiliate.credit-section.btn-edit-menu-trigger',
      menuItem: (key: string) => `crm-affiliate.credit-section.menu-${key}` as const,
      deleteBtn: 'crm-affiliate.credit-section.btn-delete',
      addCreditsBtn: 'crm-affiliate.credit-section.btn-add-credits',
    },
    personalDetails: {
      fullNameReadonly: 'crm-affiliate.personal-details.readonly-full-name',
      nationalIdReadonly: 'crm-affiliate.personal-details.readonly-national-id',
      viewDoc: (id: number | string) => `crm-affiliate.personal-details.btn-view-doc-${id}` as const,
      deleteDoc: (id: number | string) => `crm-affiliate.personal-details.btn-delete-doc-${id}` as const,
      chooseFileInput: (type: string) => `crm-affiliate.personal-details.input-choose-file-${type}` as const,
      chooseFileBtn: (type: string) => `crm-affiliate.personal-details.btn-choose-file-${type}` as const,
    },
    contactDetails: {
      phoneReadonly: 'crm-affiliate.contact-details.readonly-phone',
      phoneE164Readonly: 'crm-affiliate.contact-details.readonly-phone-e164',
      dealerFinanceEmailReadonly: 'crm-affiliate.contact-details.readonly-dealer-finance-email',
    },
    addressSection: {
      editBtn: 'crm-affiliate.address-section.btn-edit',
    },
    agentSettings: {
      toggle: (key: string) => `crm-affiliate.agent-settings.switch-${key}` as const,
      level2Window: 'crm-affiliate.agent-settings.level-2-window',
    },
    agentNetwork: {
      leaderLink: 'crm-affiliate.agent-network.link-leader',
      referrerLink: 'crm-affiliate.agent-network.link-referrer',
      parentLink: 'crm-affiliate.agent-network.link-parent',
      referrerOpenProfile: 'crm-affiliate.agent-network.link-referrer-open-profile',
      parentOpenProfile: 'crm-affiliate.agent-network.link-parent-open-profile',
    },
    bankInformation: {
      branchCodeReadonly: 'crm-affiliate.bank-information.readonly-branch-code',
    },
    supportSettings: {
      teamReadonly: 'crm-affiliate.support-settings.readonly-team',
      resetLineVerificationBtn: 'crm-affiliate.support-settings.btn-reset-line-verification',
    },
    licenseRegistration: {
      oicFullNameReadonly: 'crm-affiliate.license-registration.readonly-oic-full-name',
      brokerLicenseNumberReadonly: 'crm-affiliate.license-registration.readonly-broker-license-number',
      oicVerifiedToggle: 'crm-affiliate.license-registration.switch-oic-verified',
      brokerLicenseToggle: 'crm-affiliate.license-registration.switch-broker-license',
      viewDoc: (id: number | string) => `crm-affiliate.license-registration.btn-view-doc-${id}` as const,
      deleteDoc: (id: number | string) => `crm-affiliate.license-registration.btn-delete-doc-${id}` as const,
      chooseFileInput: (type: string) => `crm-affiliate.license-registration.input-choose-file-${type}` as const,
      chooseFileBtn: (type: string) => `crm-affiliate.license-registration.btn-choose-file-${type}` as const,
    },
    paymentSettings: {
      toggle: (key: string) => `crm-affiliate.payment-settings.switch-${key}` as const,
    },
    cmiSettings: {
      editBtn: 'crm-affiliate.cmi-settings.btn-edit',
    },
    miscellaneous: {
      activeCheckbox: 'crm-affiliate.miscellaneous.checkbox-active',
      previousPolicyEndDateReadonly: 'crm-affiliate.miscellaneous.readonly-previous-policy-end-date',
      viewDoc: (id: number | string) => `crm-affiliate.miscellaneous.btn-view-doc-${id}` as const,
      deleteDoc: (id: number | string) => `crm-affiliate.miscellaneous.btn-delete-doc-${id}` as const,
      chooseFileInput: (type: string) => `crm-affiliate.miscellaneous.input-choose-file-${type}` as const,
      chooseFileBtn: (type: string) => `crm-affiliate.miscellaneous.btn-choose-file-${type}` as const,
    },
    addCreditModal: {
      limitInput: 'crm-affiliate.add-credit-modal.input-limit',
      availableInput: 'crm-affiliate.add-credit-modal.input-available',
      prepaidCheckbox: 'crm-affiliate.add-credit-modal.checkbox-prepaid',
      cancelBtn: 'crm-affiliate.add-credit-modal.btn-cancel',
      submitBtn: 'crm-affiliate.add-credit-modal.btn-submit',
    },
    blockAgentModal: {
      accountsSelect: 'crm-affiliate.block-agent-modal.combobox-accounts',
      reasonTextarea: 'crm-affiliate.block-agent-modal.input-reason',
      cancelBtn: 'crm-affiliate.block-agent-modal.btn-cancel',
      confirmBtn: 'crm-affiliate.block-agent-modal.btn-confirm',
    },
    editAddressSheet: {
      firstLineInput: (which: string) => `crm-affiliate.edit-address-sheet.input-first-line-${which}` as const,
      postalCodeInput: (which: string) => `crm-affiliate.edit-address-sheet.input-postal-code-${which}` as const,
      sameAsIdToggle: 'crm-affiliate.edit-address-sheet.switch-same-as-id',
      cancelBtn: 'crm-affiliate.edit-address-sheet.btn-cancel',
      saveBtn: 'crm-affiliate.edit-address-sheet.btn-save',
    },
    editCmiModal: {
      insurersSelect: 'crm-affiliate.edit-cmi-modal.combobox-insurers',
      cancelBtn: 'crm-affiliate.edit-cmi-modal.btn-cancel',
      saveBtn: 'crm-affiliate.edit-cmi-modal.btn-save',
    },
    editCreditModal: {
      amountInput: 'crm-affiliate.edit-credit-modal.input-amount',
      cancelBtn: 'crm-affiliate.edit-credit-modal.btn-cancel',
      submitBtn: 'crm-affiliate.edit-credit-modal.btn-submit',
    },
    userActionsModal: {
      levelSelect: 'crm-affiliate.user-actions-modal.select-level',
      reasonInput: 'crm-affiliate.user-actions-modal.input-reason',
      saveBtn: 'crm-affiliate.user-actions-modal.btn-save',
      confirmCancelBtn: 'crm-affiliate.user-actions-modal.btn-confirm-cancel',
      confirmUpgradeBtn: 'crm-affiliate.user-actions-modal.btn-confirm-upgrade',
    },
    commissionsModal: {
      insurerFilterSelect: 'crm-affiliate.commissions-modal.select-insurer-filter',
      productFilterSelect: 'crm-affiliate.commissions-modal.select-product-filter',
      vmiClassFilterSelect: 'crm-affiliate.commissions-modal.select-vmi-class-filter',
      vehicleCategoryFilterSelect: 'crm-affiliate.commissions-modal.select-vehicle-category-filter',
      garageTypeFilterSelect: 'crm-affiliate.commissions-modal.select-garage-type-filter',
      scheduleBtn: (index: number) => `crm-affiliate.commissions-modal.btn-schedule-${index}` as const,
      prevPageBtn: 'crm-affiliate.commissions-modal.btn-prev-page',
      nextPageBtn: 'crm-affiliate.commissions-modal.btn-next-page',
    },
    scheduleCommissionModal: {
      validFromDate: (index: number) => `crm-affiliate.schedule-commission-modal.input-valid-from-${index}` as const,
      validTillDate: (index: number) => `crm-affiliate.schedule-commission-modal.input-valid-till-${index}` as const,
      typeSelect: (index: number) => `crm-affiliate.schedule-commission-modal.select-type-${index}` as const,
      valueInput: (index: number) => `crm-affiliate.schedule-commission-modal.input-value-${index}` as const,
      addRowBtn: 'crm-affiliate.schedule-commission-modal.btn-add-row',
      removeRowBtn: (index: number) => `crm-affiliate.schedule-commission-modal.btn-remove-row-${index}` as const,
      cancelBtn: 'crm-affiliate.schedule-commission-modal.btn-cancel',
      applyBtn: 'crm-affiliate.schedule-commission-modal.btn-apply',
    },
    invoiceModal: {
      compulsory: {
        selectAllCheckbox: 'crm-affiliate.invoice-modal.checkbox-select-all-compulsory',
        rowCheckbox: (id: number | string) => `crm-affiliate.invoice-modal.checkbox-row-compulsory-${id}` as const,
        prevPageBtn: 'crm-affiliate.invoice-modal.btn-prev-page-compulsory',
        nextPageBtn: 'crm-affiliate.invoice-modal.btn-next-page-compulsory',
        pageSizeSelect: 'crm-affiliate.invoice-modal.select-page-size-compulsory',
        generateBtn: 'crm-affiliate.invoice-modal.btn-generate-compulsory',
      },
      voluntary: {
        rowCheckbox: (key: string) => `crm-affiliate.invoice-modal.checkbox-row-voluntary-${key}` as const,
        generateBtn: 'crm-affiliate.invoice-modal.btn-generate-voluntary',
      },
    },
    subagentModal: {
      tab: (key: string) => `crm-affiliate.subagent-modal.tab-${key}` as const,
      fullNameInput: 'crm-affiliate.subagent-modal.input-full-name',
      emailInput: 'crm-affiliate.subagent-modal.input-email',
      phoneInput: 'crm-affiliate.subagent-modal.input-phone',
      nationalIdInput: 'crm-affiliate.subagent-modal.input-national-id',
      dobTrigger: 'crm-affiliate.subagent-modal.btn-dob',
      genderRadio: (value: string) => `crm-affiliate.subagent-modal.radio-gender-${value}` as const,
      creditsInput: 'crm-affiliate.subagent-modal.input-credits',
      copyAddressCheckbox: 'crm-affiliate.subagent-modal.checkbox-copy-address',
      addressFirstLineInput: (prefix: string) => `crm-affiliate.subagent-modal.input-first-line-${prefix}` as const,
      addressProvinceSelect: (prefix: string) => `crm-affiliate.subagent-modal.select-province-${prefix}` as const,
      addressDistrictSelect: (prefix: string) => `crm-affiliate.subagent-modal.select-district-${prefix}` as const,
      addressSubdistrictSelect: (prefix: string) =>
        `crm-affiliate.subagent-modal.select-subdistrict-${prefix}` as const,
      addressPostalCodeInput: (prefix: string) => `crm-affiliate.subagent-modal.input-postal-code-${prefix}` as const,
      formCancelBtn: 'crm-affiliate.subagent-modal.btn-form-cancel',
      formSubmitBtn: 'crm-affiliate.subagent-modal.btn-form-submit',
      commissionInsurerFilterSelect: 'crm-affiliate.subagent-modal.select-commission-insurer-filter',
      commissionProductFilterSelect: 'crm-affiliate.subagent-modal.select-commission-product-filter',
      commissionValueInput: (index: number) => `crm-affiliate.subagent-modal.input-commission-value-${index}` as const,
      commissionResetBtn: 'crm-affiliate.subagent-modal.btn-commission-reset',
      commissionCloseBtn: 'crm-affiliate.subagent-modal.btn-commission-close',
      commissionSaveBtn: 'crm-affiliate.subagent-modal.btn-commission-save',
    },
    crmDetails: {
      tab: (key: string) => `crm-affiliate.crm-details.tab-${key}` as const,
      tasks: {
        addTaskBtn: 'crm-affiliate.crm-details.btn-add-task',
      },
      interactions: {
        addBtn: 'crm-affiliate.crm-details.btn-add-interaction',
        rowMenuTrigger: (id: number | string) =>
          `crm-affiliate.crm-details.btn-interaction-menu-trigger-${id}` as const,
        rowEditItem: (id: number | string) => `crm-affiliate.crm-details.menu-interaction-edit-${id}` as const,
      },
      moreDetails: {
        field: (name: string) => `crm-affiliate.crm-details.input-more-details-${name}` as const,
        radioOption: (name: string, value: string) =>
          `crm-affiliate.crm-details.radio-more-details-${name}-${value}` as const,
        checkboxOption: (name: string, value: string) =>
          `crm-affiliate.crm-details.checkbox-more-details-${name}-${value}` as const,
        cancel: (name: string) => `crm-affiliate.crm-details.btn-more-details-cancel-${name}` as const,
        confirm: (name: string) => `crm-affiliate.crm-details.btn-more-details-confirm-${name}` as const,
      },
    },
    dashboard: {
      tab: (key: string) => `crm-affiliate.dashboard.tab-${key}` as const,
      commissionsTab: (key: string) => `crm-affiliate.dashboard.tab-commissions-${key}` as const,
      referralsTab: (key: string) => `crm-affiliate.dashboard.tab-referrals-${key}` as const,
    },
  },

  // ---- crm module (Wave 4) ----
  // src/modules/crm/** (excluding agent-detail)
  crm: {
    header: {
      addTask: 'crm.header.btn-add-task',
    },
    filters: {
      allFilters: 'crm.filters.btn-all-filters',
      cancel: 'crm.filters.btn-cancel',
      clear: 'crm.filters.btn-clear',
      apply: 'crm.filters.btn-apply',
      clearAll: 'crm.filters.btn-clear-all',
    },
    table: {
      row: (id: number | string) => `crm.table.row-${id}` as const,
      selectAll: 'crm.table.checkbox-select-all',
      select: (id: number | string) => `crm.table.checkbox-select-${id}` as const,
      expand: (id: number | string) => `crm.table.btn-expand-${id}` as const,
      reassign: 'crm.table.btn-reassign',
      markCompleted: 'crm.table.btn-mark-completed',
    },
    actionsColumn: {
      trigger: (id: number | string) => `crm.actions-column.btn-menu-trigger-${id}` as const,
      item: (id: number | string, action: string) => `crm.actions-column.btn-${action}-${id}` as const,
    },
    addTaskForm: {
      agent: 'crm.add-task-form.select-agent',
      agentSearch: 'crm.add-task-form.input-agent-search',
      task: 'crm.add-task-form.select-task',
      etaDate: 'crm.add-task-form.datepicker-eta',
      etaTime: 'crm.add-task-form.btn-eta-time',
      description: 'crm.add-task-form.input-description',
      statusPending: 'crm.add-task-form.radio-status-pending',
      statusCompleted: 'crm.add-task-form.radio-status-completed',
      cancel: 'crm.add-task-form.btn-cancel',
      submit: 'crm.add-task-form.btn-submit',
    },
    addInteractionDialog: {
      tabInteraction: 'crm.add-interaction-dialog.tab-interaction',
      tabSurvey: 'crm.add-interaction-dialog.tab-survey',
    },
    addInteractionForm: {
      cancel: 'crm.add-interaction-form.btn-cancel',
      submit: 'crm.add-interaction-form.btn-submit',
    },
    interactionDetailsSection: {
      staff: 'crm.interaction-details-section.combobox-staff',
      type: 'crm.interaction-details-section.combobox-type',
      date: 'crm.interaction-details-section.datepicker-interacted-at',
      time: 'crm.interaction-details-section.btn-interacted-at-time',
      successful: 'crm.interaction-details-section.checkbox-successful',
      remarks: 'crm.interaction-details-section.combobox-remarks',
      additionalRemarks: 'crm.interaction-details-section.input-additional-remarks',
    },
    followUpSection: {
      toggle: 'crm.follow-up-section.toggle-is-follow-up',
      date: 'crm.follow-up-section.datepicker-follow-up-at',
      time: 'crm.follow-up-section.btn-follow-up-time',
    },
    profileSection: {
      agentType: 'crm.profile-section.combobox-agent-type',
      hasPhysicalLocation: 'crm.profile-section.checkbox-has-physical-location',
      hasBrokerLicense: 'crm.profile-section.checkbox-has-broker-license',
      experience: 'crm.profile-section.combobox-experience',
      occupation: 'crm.profile-section.combobox-occupation',
      wantToDoMlm: 'crm.profile-section.checkbox-want-to-do-mlm',
      lineId: 'crm.profile-section.input-line-id',
    },
    quotationPreferenceSection: {
      preference: (value: string) => `crm.quotation-preference-section.radio-preference-${value}` as const,
      preferredType: (value: string) => `crm.quotation-preference-section.radio-preferred-type-${value}` as const,
      insuranceClass: (value: string) => `crm.quotation-preference-section.checkbox-insurance-class-${value}` as const,
    },
    affiliationsSection: {
      sellingForOtherInsurers: 'crm.affiliations-section.input-selling-for-other-insurers',
      isSellingForOtherBrokers: 'crm.affiliations-section.checkbox-is-selling-for-other-brokers',
    },
    surveySection: {
      answer: (questionId: number | string) => `crm.survey-section.input-answer-${questionId}` as const,
      radio: (questionId: number | string, optionId: number | string) =>
        `crm.survey-section.radio-${questionId}-${optionId}` as const,
      checkbox: (questionId: number | string, optionId: number | string) =>
        `crm.survey-section.checkbox-${questionId}-${optionId}` as const,
      uploadFile: (questionId: number | string) => `crm.survey-section.btn-upload-${questionId}` as const,
      cancel: 'crm.survey-section.btn-cancel',
      submit: 'crm.survey-section.btn-submit',
    },
    reassignTasksModal: {
      staffSelect: 'crm.reassign-tasks-modal.select-staff',
      cancel: 'crm.reassign-tasks-modal.btn-cancel',
      submit: 'crm.reassign-tasks-modal.btn-submit',
    },
    markCompletedModal: {
      cancel: 'crm.mark-completed-modal.btn-cancel',
      submit: 'crm.mark-completed-modal.btn-submit',
    },
  },
  debtCollection: {
    filters: {
      allFilters: 'debt-collection.filters.btn-all-filters',
      cancel: 'debt-collection.filters.btn-cancel',
      clear: 'debt-collection.filters.btn-clear',
      apply: 'debt-collection.filters.btn-apply',
      clearAll: 'debt-collection.filters.btn-clear-all',
    },
    exportDropdown: {
      trigger: 'debt-collection.export-dropdown.btn-trigger',
      exportTasks: 'debt-collection.export-dropdown.menu-export-tasks',
      exportReport: 'debt-collection.export-dropdown.menu-export-report',
    },
    table: {
      row: (id: number | string) => `debt-collection.table.row-${id}` as const,
      saleGroup: (saleId: number | string) => `debt-collection.table.group-${saleId}` as const,
      btnToggleGroup: (saleId: number | string) => `debt-collection.table.btn-toggle-group-${saleId}` as const,
      btnViewSale: (saleId: number | string) => `debt-collection.table.btn-view-sale-${saleId}` as const,
      btnPin: (columnId: string) => `debt-collection.table.btn-pin-${columnId}` as const,
      btnSort: (columnId: string) => `debt-collection.table.btn-sort-${columnId}` as const,
      action: (id: number | string, action: string) => `debt-collection.table.btn-${action}-${id}` as const,
      linkRemark: (id: number | string, index: number) => `debt-collection.table.link-remark-${id}-${index}` as const,
    },
    taskEditDialog: {
      status: 'debt-collection.task-edit-dialog.select-status',
      eta: 'debt-collection.task-edit-dialog.btn-eta',
      description: 'debt-collection.task-edit-dialog.input-description',
      cancel: 'debt-collection.task-edit-dialog.btn-cancel',
      submit: 'debt-collection.task-edit-dialog.btn-submit',
    },
    remarksDialog: {
      newRemark: 'debt-collection.remarks-dialog.input-new-remark',
      close: 'debt-collection.remarks-dialog.btn-close',
      addRemark: 'debt-collection.remarks-dialog.btn-add-remark',
      itemEdit: (id: number | string) => `debt-collection.remarks-dialog.btn-edit-${id}` as const,
      itemDelete: (id: number | string) => `debt-collection.remarks-dialog.btn-delete-${id}` as const,
      itemSave: (id: number | string) => `debt-collection.remarks-dialog.btn-save-${id}` as const,
      itemCancel: (id: number | string) => `debt-collection.remarks-dialog.btn-cancel-${id}` as const,
      itemTextarea: (id: number | string) => `debt-collection.remarks-dialog.input-edit-${id}` as const,
      itemLink: (id: number | string, index: number) =>
        `debt-collection.remarks-dialog.link-comment-${id}-${index}` as const,
      deleteConfirmCancel: 'debt-collection.remarks-dialog.btn-delete-confirm-cancel',
      deleteConfirmConfirm: 'debt-collection.remarks-dialog.btn-delete-confirm-confirm',
    },
  },

  // ---- voluntary-insurance module (Wave 4) ----
  // src/modules/voluntary-insurance/**
  // Route: /voluntary-insurance/:tab?/:subTab? — a tabbed pipeline page. Top-level
  // tabs (index.tsx) are `leads` + policy stages; `leads` has its own new-leads /
  // renewals sub-tabs (leads/index.tsx). NOTE: `newLeads`/`leadsTabs`-style keys here
  // are intentionally nested under `voluntaryInsurance` to avoid colliding with the
  // UNRELATED top-level `testIds.newLeads` / `testIds.renewals` (Wave 2, generic
  // /leads module) — same english names, different feature, different parent object.
  voluntaryInsurance: {
    // Primary pipeline tabs — index.tsx (leads / all_policies / payment / ...)
    tabs: { tab: (key: string) => `voluntary-insurance.tabs.tab-${key}` as const },

    // Leads tab shell — leads/index.tsx (new-leads vs renewals sub-tabs + shared controls)
    leadsTabs: {
      tab: (key: string) => `voluntary-insurance.leads-tabs.tab-${key}` as const,
      dateChip: 'voluntary-insurance.leads-tabs.btn-date-chip',
      search: 'voluntary-insurance.leads-tabs.input-search',
    },

    // Policies tab shell — policies/index.tsx (stage tabs come from `tabs` above)
    policiesTabs: {
      dateChip: 'voluntary-insurance.policies-tabs.btn-date-chip',
      search: 'voluntary-insurance.policies-tabs.input-search',
    },

    newLeads: {
      filters: {
        allFilters: 'voluntary-insurance.new-leads.filters.btn-all-filters',
        cancel: 'voluntary-insurance.new-leads.filters.btn-cancel',
        clear: 'voluntary-insurance.new-leads.filters.btn-clear',
        apply: 'voluntary-insurance.new-leads.filters.btn-apply',
      },
      table: {
        row: (id: number | string) => `voluntary-insurance.new-leads.table.row-${id}` as const,
        expand: (id: number | string) => `voluntary-insurance.new-leads.table.btn-expand-${id}` as const,
        menuTrigger: (id: number | string) => `voluntary-insurance.new-leads.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) =>
          `voluntary-insurance.new-leads.table.btn-${action}-${id}` as const,
      },
      insurerSubTable: {
        status: (id: number | string) => `voluntary-insurance.new-leads.insurer-sub-table.select-status-${id}` as const,
        kebabTrigger: (id: number | string) =>
          `voluntary-insurance.new-leads.insurer-sub-table.btn-kebab-${id}` as const,
        kebabItem: (id: number | string, action: string) =>
          `voluntary-insurance.new-leads.insurer-sub-table.btn-${action}-${id}` as const,
        rejectRemark: (id: number | string) =>
          `voluntary-insurance.new-leads.insurer-sub-table.reject-remark-${id}` as const,
        rejectCancel: (id: number | string) =>
          `voluntary-insurance.new-leads.insurer-sub-table.btn-reject-cancel-${id}` as const,
        rejectSave: (id: number | string) =>
          `voluntary-insurance.new-leads.insurer-sub-table.btn-reject-save-${id}` as const,
      },
      appliedFilterChips: {
        trigger: (key: string) => `voluntary-insurance.new-leads.applied-filter-chips.btn-trigger-${key}` as const,
        remove: (key: string) => `voluntary-insurance.new-leads.applied-filter-chips.btn-remove-${key}` as const,
        clearAll: 'voluntary-insurance.new-leads.applied-filter-chips.btn-clear-all',
      },
    },

    renewals: {
      filters: {
        allFilters: 'voluntary-insurance.renewals.filters.btn-all-filters',
        cancel: 'voluntary-insurance.renewals.filters.btn-cancel',
        clear: 'voluntary-insurance.renewals.filters.btn-clear',
        apply: 'voluntary-insurance.renewals.filters.btn-apply',
      },
      table: {
        row: (id: number | string) => `voluntary-insurance.renewals.table.row-${id}` as const,
        expand: (id: number | string) => `voluntary-insurance.renewals.table.btn-expand-${id}` as const,
        menuTrigger: (id: number | string) => `voluntary-insurance.renewals.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) =>
          `voluntary-insurance.renewals.table.btn-${action}-${id}` as const,
      },
      appliedFilterChips: {
        trigger: (key: string) => `voluntary-insurance.renewals.applied-filter-chips.btn-trigger-${key}` as const,
        remove: (key: string) => `voluntary-insurance.renewals.applied-filter-chips.btn-remove-${key}` as const,
        clearAll: 'voluntary-insurance.renewals.applied-filter-chips.btn-clear-all',
      },
      statusColumn: {
        select: (id: number | string) => `voluntary-insurance.renewals.status-column.select-status-${id}` as const,
        confirmCancel: (id: number | string) => `voluntary-insurance.renewals.status-column.btn-cancel-${id}` as const,
        confirmConfirm: (id: number | string) =>
          `voluntary-insurance.renewals.status-column.btn-confirm-${id}` as const,
      },
      emailModal: {
        sender: 'voluntary-insurance.renewals.email-modal.select-sender',
        to: 'voluntary-insurance.renewals.email-modal.input-to',
        subject: 'voluntary-insurance.renewals.email-modal.input-subject',
        body: 'voluntary-insurance.renewals.email-modal.input-body',
        attachFileInput: 'voluntary-insurance.renewals.email-modal.input-file',
        attach: 'voluntary-insurance.renewals.email-modal.btn-attach',
        removeAttachment: (index: number) =>
          `voluntary-insurance.renewals.email-modal.btn-remove-attachment-${index}` as const,
        cancel: 'voluntary-insurance.renewals.email-modal.btn-cancel',
        send: 'voluntary-insurance.renewals.email-modal.btn-send',
      },
      expandedRow: {
        claimStatus: (id: number | string) =>
          `voluntary-insurance.renewals.expanded-row.select-claim-status-${id}` as const,
        previewDoc: (id: number | string) => `voluntary-insurance.renewals.expanded-row.btn-preview-${id}` as const,
        downloadDoc: (id: number | string) => `voluntary-insurance.renewals.expanded-row.btn-download-${id}` as const,
        deleteDoc: (id: number | string) => `voluntary-insurance.renewals.expanded-row.btn-delete-${id}` as const,
      },
    },

    policies: {
      filters: {
        allFilters: 'voluntary-insurance.policies.filters.btn-all-filters',
        cancel: 'voluntary-insurance.policies.filters.btn-cancel',
        clear: 'voluntary-insurance.policies.filters.btn-clear',
        apply: 'voluntary-insurance.policies.filters.btn-apply',
      },
      table: {
        row: (id: number | string) => `voluntary-insurance.policies.table.row-${id}` as const,
        expand: (id: number | string) => `voluntary-insurance.policies.table.btn-expand-${id}` as const,
        menuTrigger: (id: number | string) => `voluntary-insurance.policies.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) =>
          `voluntary-insurance.policies.table.btn-${action}-${id}` as const,
      },
      appliedFilterChips: {
        trigger: (key: string) => `voluntary-insurance.policies.applied-filter-chips.btn-trigger-${key}` as const,
        remove: (key: string) => `voluntary-insurance.policies.applied-filter-chips.btn-remove-${key}` as const,
        clearAll: 'voluntary-insurance.policies.applied-filter-chips.btn-clear-all',
      },
      status: {
        select: (id: number | string) => `voluntary-insurance.policies.status.select-${id}` as const,
        reworkButton: (id: number | string) => `voluntary-insurance.policies.status.btn-rework-${id}` as const,
      },
      // PolicySubRows.tsx (expanded row VMI/CMI sub-table) — `id` is the caller's
      // composite `${policyType}-${policy.id}` since a single lead can have both a
      // VMI and CMI row that fall back to the same underlying id pre-issuance.
      subRows: {
        select: (id: string) => `voluntary-insurance.policies.sub-rows.select-status-${id}` as const,
        reworkButton: (id: string) => `voluntary-insurance.policies.sub-rows.btn-rework-${id}` as const,
        claim: (column: string, id: string) =>
          `voluntary-insurance.policies.sub-rows.btn-claim-${column}-${id}` as const,
      },
    },

    historyActivity: {
      mentionDropdown: 'voluntary-insurance.history-activity.menu-mention-dropdown',
      policyFilter: 'voluntary-insurance.history-activity.select-policy-filter',
      tab: (key: string) => `voluntary-insurance.history-activity.tab-${key}` as const,
      scrollToLatest: 'voluntary-insurance.history-activity.btn-scroll-to-latest',
      addRemarkButton: 'voluntary-insurance.history-activity.btn-add-remark',
      addReworkButton: 'voluntary-insurance.history-activity.btn-add-rework',
      addRemarkForm: {
        close: 'voluntary-insurance.history-activity.add-remark-form.btn-close',
        policyVmi: 'voluntary-insurance.history-activity.add-remark-form.radio-vmi',
        policyCmi: 'voluntary-insurance.history-activity.add-remark-form.radio-cmi',
        remark: 'voluntary-insurance.history-activity.add-remark-form.input-remark',
        removeFile: (index: number) =>
          `voluntary-insurance.history-activity.add-remark-form.btn-remove-file-${index}` as const,
        attach: 'voluntary-insurance.history-activity.add-remark-form.input-file',
        cancel: 'voluntary-insurance.history-activity.add-remark-form.btn-cancel',
        submit: 'voluntary-insurance.history-activity.add-remark-form.btn-submit',
      },
      addReworkForm: {
        close: 'voluntary-insurance.history-activity.add-rework-form.btn-close',
        policyVmi: 'voluntary-insurance.history-activity.add-rework-form.radio-vmi',
        policyCmi: 'voluntary-insurance.history-activity.add-rework-form.radio-cmi',
        reason: 'voluntary-insurance.history-activity.add-rework-form.select-reason',
        details: 'voluntary-insurance.history-activity.add-rework-form.input-details',
        autoResolveDate: 'voluntary-insurance.history-activity.add-rework-form.input-auto-resolve-date',
        manualOverrideTrigger: 'voluntary-insurance.history-activity.add-rework-form.combobox-manual-override',
        manualOverrideSearch: 'voluntary-insurance.history-activity.add-rework-form.input-manual-override-search',
        manualOverrideItem: (id: number | string) =>
          `voluntary-insurance.history-activity.add-rework-form.row-manual-override-${id}` as const,
        removeFile: (index: number) =>
          `voluntary-insurance.history-activity.add-rework-form.btn-remove-file-${index}` as const,
        attach: 'voluntary-insurance.history-activity.add-rework-form.input-file',
        cancel: 'voluntary-insurance.history-activity.add-rework-form.btn-cancel',
        submit: 'voluntary-insurance.history-activity.add-rework-form.btn-submit',
      },
      reassignReworkForm: {
        close: 'voluntary-insurance.history-activity.reassign-rework-form.btn-close',
        reasonTrigger: 'voluntary-insurance.history-activity.reassign-rework-form.combobox-reason',
        reasonSearch: 'voluntary-insurance.history-activity.reassign-rework-form.input-reason-search',
        reasonItem: (id: number | string) =>
          `voluntary-insurance.history-activity.reassign-rework-form.row-reason-${id}` as const,
        details: 'voluntary-insurance.history-activity.reassign-rework-form.input-details',
        autoResolveDate: 'voluntary-insurance.history-activity.reassign-rework-form.input-auto-resolve-date',
        manualOverrideTrigger: 'voluntary-insurance.history-activity.reassign-rework-form.combobox-manual-override',
        manualOverrideSearch: 'voluntary-insurance.history-activity.reassign-rework-form.input-manual-override-search',
        manualOverrideItem: (id: number | string) =>
          `voluntary-insurance.history-activity.reassign-rework-form.row-manual-override-${id}` as const,
        removeFile: (index: number) =>
          `voluntary-insurance.history-activity.reassign-rework-form.btn-remove-file-${index}` as const,
        attach: 'voluntary-insurance.history-activity.reassign-rework-form.input-file',
        cancel: 'voluntary-insurance.history-activity.reassign-rework-form.btn-cancel',
        submit: 'voluntary-insurance.history-activity.reassign-rework-form.btn-submit',
      },
      // ReplyForm.tsx renders once per open reply thread — keyed by the timeline
      // item's targetId so multiple reply forms in the same timeline can't collide.
      replyForm: {
        close: (targetId: number | string) =>
          `voluntary-insurance.history-activity.reply-form.btn-close-${targetId}` as const,
        content: (targetId: number | string) =>
          `voluntary-insurance.history-activity.reply-form.input-content-${targetId}` as const,
        removeFile: (targetId: number | string, index: number) =>
          `voluntary-insurance.history-activity.reply-form.btn-remove-file-${targetId}-${index}` as const,
        attach: (targetId: number | string) =>
          `voluntary-insurance.history-activity.reply-form.input-file-${targetId}` as const,
        cancel: (targetId: number | string) =>
          `voluntary-insurance.history-activity.reply-form.btn-cancel-${targetId}` as const,
        submit: (targetId: number | string) =>
          `voluntary-insurance.history-activity.reply-form.btn-submit-${targetId}` as const,
      },
      timeline: {
        attachmentTile: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-attachment-${id}` as const,
        remarkSeeMore: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-see-more-${id}` as const,
        remarkToggleReplies: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-toggle-replies-${id}` as const,
        remarkReply: (id: number | string) => `voluntary-insurance.history-activity.timeline.btn-reply-${id}` as const,
        remarkDownload: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-remark-download-${id}` as const,
        replyDownload: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-reply-download-${id}` as const,
        reworkSeeMore: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-rework-see-more-${id}` as const,
        reworkToggleReplies: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-rework-toggle-replies-${id}` as const,
        reworkReply: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-rework-reply-${id}` as const,
        reworkDownload: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-rework-download-${id}` as const,
        reworkReassign: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-rework-reassign-${id}` as const,
        reworkResolve: (id: number | string) =>
          `voluntary-insurance.history-activity.timeline.btn-rework-resolve-${id}` as const,
      },
    },

    // Reusable pieces shared across new-leads / renewals / policies tables — src/modules/voluntary-insurance/shared/**
    shared: {
      table: {
        expandAll: 'voluntary-insurance.shared.table.btn-expand-all',
        expand: (id: number | string) => `voluntary-insurance.shared.table.btn-expand-${id}` as const,
      },
      claimRf: (id: number | string) => `voluntary-insurance.shared.rf-column.btn-claim-${id}` as const,
      claimSc: (id: number | string) => `voluntary-insurance.shared.sc-column.btn-claim-${id}` as const,
      // EmployeeHoverCard.tsx (EditableEmployeeHoverCard) — reused for RF/SC/DE/Admin/Delivery
      // cells, so `column` disambiguates cells within the same table row.
      employeeHoverCard: {
        trigger: (column: string, id: number | string) =>
          `voluntary-insurance.shared.employee-hover-card.btn-trigger-${column}-${id}` as const,
        update: (column: string, id: number | string) =>
          `voluntary-insurance.shared.employee-hover-card.btn-update-${column}-${id}` as const,
      },
      claimEmployee: (column: string, id: number | string) =>
        `voluntary-insurance.shared.claim-employee.btn-claim-${column}-${id}` as const,
      // Single dialog instance app-wide (one cell's dialog is open at a time).
      changeEmployeeDialog: {
        search: 'voluntary-insurance.shared.change-employee-dialog.input-search',
        item: (id: number | string) => `voluntary-insurance.shared.change-employee-dialog.row-${id}` as const,
        cancel: 'voluntary-insurance.shared.change-employee-dialog.btn-cancel',
        submit: 'voluntary-insurance.shared.change-employee-dialog.btn-submit',
      },
      ownerFilterChips: {
        trigger: 'voluntary-insurance.shared.owner-filter-chips.btn-trigger',
        remove: 'voluntary-insurance.shared.owner-filter-chips.btn-remove',
      },
      pipelineEmptyState: {
        clearAll: 'voluntary-insurance.shared.pipeline-empty-state.btn-clear-all',
      },
    },
  },

  // ---- non-motor-policies module (Wave 4) ----
  // src/modules/non-motor-policies/**
  nonStandardLeads: {
    filters: {
      download: 'non-standard-leads.filters.btn-download',
      allFilters: 'non-standard-leads.filters.btn-all-filters',
      cancel: 'non-standard-leads.filters.btn-cancel',
      clear: 'non-standard-leads.filters.btn-clear',
      apply: 'non-standard-leads.filters.btn-apply',
      clearAll: 'non-standard-leads.filters.btn-clear-all',
    },
    table: {
      row: (id: number | string) => `non-standard-leads.table.row-${id}` as const,
      expand: (id: number | string) => `non-standard-leads.table.btn-expand-${id}` as const,
      sortHeader: (columnId: string) => `non-standard-leads.table.btn-sort-${columnId}` as const,
      action: (id: number | string, action: string) => `non-standard-leads.table.btn-${action}-${id}` as const,
    },
  },
  nonStandardInProgress: {
    filters: {
      download: 'non-standard-in-progress.filters.btn-download',
      allFilters: 'non-standard-in-progress.filters.btn-all-filters',
      cancel: 'non-standard-in-progress.filters.btn-cancel',
      clear: 'non-standard-in-progress.filters.btn-clear',
      apply: 'non-standard-in-progress.filters.btn-apply',
      clearAll: 'non-standard-in-progress.filters.btn-clear-all',
    },
    table: {
      row: (id: number | string) => `non-standard-in-progress.table.row-${id}` as const,
      expand: (id: number | string) => `non-standard-in-progress.table.btn-expand-${id}` as const,
      sortHeader: (columnId: string) => `non-standard-in-progress.table.btn-sort-${columnId}` as const,
      action: (id: number | string, action: string) => `non-standard-in-progress.table.btn-${action}-${id}` as const,
    },
  },
  nonMotorLeads: {
    filters: {
      allFilters: 'non-motor-leads.filters.btn-all-filters',
      cancel: 'non-motor-leads.filters.btn-cancel',
      clear: 'non-motor-leads.filters.btn-clear',
      apply: 'non-motor-leads.filters.btn-apply',
      clearAll: 'non-motor-leads.filters.btn-clear-all',
    },
    table: {
      row: (id: number | string) => `non-motor-leads.table.row-${id}` as const,
      expand: (id: number | string) => `non-motor-leads.table.btn-expand-${id}` as const,
      sortHeader: (columnId: string) => `non-motor-leads.table.btn-sort-${columnId}` as const,
      action: (id: number | string, action: string) => `non-motor-leads.table.btn-${action}-${id}` as const,
    },
  },

  // ---- commission-and-promotions module (Wave 4) ----
  // src/modules/commission-and-promotions/**
  commissionAndPromotions: {
    tabs: {
      tab: (key: string) => `commission-and-promotions.tabs.tab-${key}` as const,
    },
    // Tabbed page shell currently has a single implemented tab ("agent-transfers"); nested here
    // rather than as a flat sibling since the route (/commission-and-promotions/:type) and page
    // shell are shared by all future tabs.
    agentTransfers: {
      filters: {
        clearAll: 'commission-and-promotions.agent-transfers.filters.btn-clear-all',
      },
      table: {
        row: (id: number | string) => `commission-and-promotions.agent-transfers.table.row-${id}` as const,
        expand: (id: number | string) => `commission-and-promotions.agent-transfers.table.btn-expand-${id}` as const,
      },
      expandedRow: {
        viewCmiDetails: (id: number | string) =>
          `commission-and-promotions.agent-transfers.expanded-row.btn-view-cmi-${id}` as const,
        viewVehicle: (id: number | string) =>
          `commission-and-promotions.agent-transfers.expanded-row.btn-view-vehicle-${id}` as const,
      },
    },
  },

  // ---- ir-settings module (Wave 5) ----
  // src/modules/ir-settings/** — tabbed page shell (COA form, insurer tier, insurer
  // address, affiliate-specific commission rates, policy-issuance SLA, pre-quote SLA,
  // pre-sale SLA)
  irSettings: {
    tabs: { tab: (key: string) => `ir-settings.tabs.tab-${key}` as const },
    coaForm: {
      table: {
        row: (id: number | string) => `ir-settings.coa-form.table.row-${id}` as const,
        menuTrigger: (id: number | string) => `ir-settings.coa-form.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) => `ir-settings.coa-form.table.btn-${action}-${id}` as const,
      },
      addDocumentModal: {
        name: 'ir-settings.coa-form.add-document-modal.input-name',
        required: 'ir-settings.coa-form.add-document-modal.checkbox-required',
        cancel: 'ir-settings.coa-form.add-document-modal.btn-cancel',
        submit: 'ir-settings.coa-form.add-document-modal.btn-submit',
      },
      editModal: {
        editDocument: (type: string, index: number) =>
          `ir-settings.coa-form.edit-modal.btn-edit-document-${type}-${index}` as const,
        deleteDocument: (type: string, index: number) =>
          `ir-settings.coa-form.edit-modal.btn-delete-document-${type}-${index}` as const,
        previewExample: (type: string, index: number) =>
          `ir-settings.coa-form.edit-modal.btn-preview-example-${type}-${index}` as const,
        removeExample: (type: string, index: number) =>
          `ir-settings.coa-form.edit-modal.btn-remove-example-${type}-${index}` as const,
        checkbox: (name: string) => `ir-settings.coa-form.edit-modal.checkbox-${name}` as const,
        field: (name: string) => `ir-settings.coa-form.edit-modal.input-${name}` as const,
        addDocument: (type: string) => `ir-settings.coa-form.edit-modal.btn-add-document-${type}` as const,
        addExampleFile: (type: string) => `ir-settings.coa-form.edit-modal.btn-add-example-file-${type}` as const,
        exampleFileInput: (type: string) => `ir-settings.coa-form.edit-modal.input-example-file-${type}` as const,
        cancel: 'ir-settings.coa-form.edit-modal.btn-cancel',
        submit: 'ir-settings.coa-form.edit-modal.btn-submit',
      },
    },
    insurer: {
      table: {
        menuTrigger: (id: number | string) => `ir-settings.insurer.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) => `ir-settings.insurer.table.btn-${action}-${id}` as const,
      },
      editAddress: {
        field: (name: string) => `ir-settings.insurer.edit-address.input-${name}` as const,
        cancel: 'ir-settings.insurer.edit-address.btn-cancel',
        submit: 'ir-settings.insurer.edit-address.btn-submit',
      },
    },
    insurerTier: {
      header: { addNew: 'ir-settings.insurer-tier.header.btn-add-new' },
      table: {
        row: (id: number | string) => `ir-settings.insurer-tier.table.row-${id}` as const,
        editAlternate: (insurerId: number | string, alternateId: number | string) =>
          `ir-settings.insurer-tier.table.btn-edit-alternate-${insurerId}-${alternateId}` as const,
        deleteAlternate: (insurerId: number | string, alternateId: number | string) =>
          `ir-settings.insurer-tier.table.btn-delete-alternate-${insurerId}-${alternateId}` as const,
      },
      alternateMappingForm: {
        primaryInsurer: 'ir-settings.insurer-tier.alternate-mapping-form.select-primary-insurer',
        addAlternate: 'ir-settings.insurer-tier.alternate-mapping-form.btn-add-alternate',
        removeAlternate: (index: number) =>
          `ir-settings.insurer-tier.alternate-mapping-form.btn-remove-alternate-${index}` as const,
        alternateInsurer: (index: number) =>
          `ir-settings.insurer-tier.alternate-mapping-form.select-alternate-insurer-${index}` as const,
        priority: (index: number) => `ir-settings.insurer-tier.alternate-mapping-form.input-priority-${index}` as const,
        validFrom: (index: number) =>
          `ir-settings.insurer-tier.alternate-mapping-form.input-valid-from-${index}` as const,
        validTill: (index: number) =>
          `ir-settings.insurer-tier.alternate-mapping-form.input-valid-till-${index}` as const,
        cancel: 'ir-settings.insurer-tier.alternate-mapping-form.btn-cancel',
        submit: 'ir-settings.insurer-tier.alternate-mapping-form.btn-submit',
      },
    },
    affiliateSpecificCommissionRates: {
      table: {
        row: (id: number | string) => `ir-settings.affiliate-specific-commission-rates.table.row-${id}` as const,
        viewFile: (id: number | string) =>
          `ir-settings.affiliate-specific-commission-rates.table.btn-view-file-${id}` as const,
      },
    },
    insurerRanking: {
      index: {
        downloadTemplate: 'ir-settings.insurer-ranking.index.btn-download-template',
        upload: 'ir-settings.insurer-ranking.index.btn-upload',
      },
      table: {
        row: (id: number | string) => `ir-settings.insurer-ranking.table.row-${id}` as const,
        downloadFile: (id: number | string) => `ir-settings.insurer-ranking.table.btn-download-file-${id}` as const,
      },
      uploadModal: {
        file: 'ir-settings.insurer-ranking.upload-modal.input-file',
        cancel: 'ir-settings.insurer-ranking.upload-modal.btn-cancel',
        submit: 'ir-settings.insurer-ranking.upload-modal.btn-submit',
      },
    },
    policyIssuanceSla: {
      filters: {
        allFilters: 'ir-settings.policy-issuance-sla.filters.btn-all-filters',
        cancel: 'ir-settings.policy-issuance-sla.filters.btn-cancel',
        clear: 'ir-settings.policy-issuance-sla.filters.btn-clear',
        apply: 'ir-settings.policy-issuance-sla.filters.btn-apply',
        clearAll: 'ir-settings.policy-issuance-sla.filters.btn-clear-all',
      },
      table: {
        menuTrigger: (id: number | string) => `ir-settings.policy-issuance-sla.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) =>
          `ir-settings.policy-issuance-sla.table.btn-${action}-${id}` as const,
      },
      editSlaForm: {
        field: (name: string) => `ir-settings.policy-issuance-sla.edit-sla-form.input-${name}` as const,
        cancel: 'ir-settings.policy-issuance-sla.edit-sla-form.btn-cancel',
        submit: 'ir-settings.policy-issuance-sla.edit-sla-form.btn-submit',
      },
    },
    preQuoteSla: {
      index: { uploadDocument: 'ir-settings.pre-quote-sla.index.btn-upload-document' },
      table: {
        viewFile: (id: number | string) => `ir-settings.pre-quote-sla.table.btn-view-file-${id}` as const,
      },
      uploadModal: {
        documentType: 'ir-settings.pre-quote-sla.upload-modal.select-document-type',
        file: 'ir-settings.pre-quote-sla.upload-modal.input-file',
        cancel: 'ir-settings.pre-quote-sla.upload-modal.btn-cancel',
        submit: 'ir-settings.pre-quote-sla.upload-modal.btn-submit',
      },
    },
    // CSV bulk-upload flow shared by the IR settings tables (this branch only — the original
    // registry predates it).
    bulkUpload: {
      downloadTemplate: 'ir-settings.bulk-upload.btn-download-template',
      fileInput: 'ir-settings.bulk-upload.input-file',
      selectFile: 'ir-settings.bulk-upload.btn-select-file',
      clearFile: 'ir-settings.bulk-upload.btn-clear-file',
      validate: 'ir-settings.bulk-upload.btn-validate',
      dialog: {
        downloadErrorReport: 'ir-settings.bulk-upload.dialog.btn-download-error-report',
        cancel: 'ir-settings.bulk-upload.dialog.btn-cancel',
        confirm: 'ir-settings.bulk-upload.dialog.btn-confirm',
        close: (phase: string) => `ir-settings.bulk-upload.dialog.btn-close-${phase}` as const,
      },
    },
    preSaleSla: {
      filters: {
        search: 'ir-settings.pre-sale-sla.filters.input-search',
        allFilters: 'ir-settings.pre-sale-sla.filters.btn-all-filters',
        quoteType: 'ir-settings.pre-sale-sla.filters.select-quote-type',
        cancel: 'ir-settings.pre-sale-sla.filters.btn-cancel',
        clear: 'ir-settings.pre-sale-sla.filters.btn-clear',
        apply: 'ir-settings.pre-sale-sla.filters.btn-apply',
        removeQuoteTypeChip: 'ir-settings.pre-sale-sla.filters.btn-remove-quote-type-chip',
        clearAll: 'ir-settings.pre-sale-sla.filters.btn-clear-all',
      },
      table: {
        menuTrigger: (id: number | string) => `ir-settings.pre-sale-sla.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) => `ir-settings.pre-sale-sla.table.btn-${action}-${id}` as const,
      },
      editModal: {
        field: (name: string) => `ir-settings.pre-sale-sla.edit-modal.input-${name}` as const,
        cancel: 'ir-settings.pre-sale-sla.edit-modal.btn-cancel',
        submit: 'ir-settings.pre-sale-sla.edit-modal.btn-submit',
      },
    },
  },

  // ---- accounting module (Wave 5) ----
  // src/modules/accounting/** — agent statements/payments, credits, insurer SOA tables,
  // instalments, WHT upload
  accounting: {
    agents: {
      tabs: { tab: (key: string) => `accounting.agents.tabs.tab-${key}` as const },
      // Payout CSV generators, shown beside the tab strip on the upload-transfer-csv tab.
      generatePayoutCsv: {
        incentive: 'accounting.agents.btn-generate-incentive-csv',
        commission: 'accounting.agents.btn-generate-commission-csv',
      },
      combinedEndorsement: {
        filters: {
          allFilters: 'accounting.agents.combined-endorsement.filters.btn-all-filters',
          cancel: 'accounting.agents.combined-endorsement.filters.btn-cancel',
          clear: 'accounting.agents.combined-endorsement.filters.btn-clear',
          apply: 'accounting.agents.combined-endorsement.filters.btn-apply',
          clearAll: 'accounting.agents.combined-endorsement.filters.btn-clear-all',
        },
        generateCsv: 'accounting.agents.combined-endorsement.btn-generate-csv',
      },
      combinedPayments: {
        uploadPayment: 'accounting.agents.combined-payments.btn-upload-payment',
        createAnother: 'accounting.agents.combined-payments.btn-create-another',
        createInvoice: 'accounting.agents.combined-payments.btn-create-invoice',
        search: 'accounting.agents.combined-payments.input-search',
        result: (id: number | string) => `accounting.agents.combined-payments.btn-result-${id}` as const,
        selectedVehicle: (id: number | string) =>
          `accounting.agents.combined-payments.btn-selected-vehicle-${id}` as const,
        remove: (id: number | string) => `accounting.agents.combined-payments.btn-remove-${id}` as const,
      },
      pendingPayments: {
        filters: {
          allFilters: 'accounting.agents.pending-payments.filters.btn-all-filters',
          cancel: 'accounting.agents.pending-payments.filters.btn-cancel',
          clear: 'accounting.agents.pending-payments.filters.btn-clear',
          apply: 'accounting.agents.pending-payments.filters.btn-apply',
          clearAll: 'accounting.agents.pending-payments.filters.btn-clear-all',
        },
        actions: {
          batchUpload: 'accounting.agents.pending-payments.actions.btn-batch-upload',
          sendNotification: 'accounting.agents.pending-payments.actions.btn-send-notification',
          upload: 'accounting.agents.pending-payments.actions.btn-upload',
        },
        table: {
          reconcile: (id: number | string) => `accounting.agents.pending-payments.table.btn-reconcile-${id}` as const,
        },
      },
      // Reusable statement-upload form (agents/shared/UploadStatementForm.tsx); callers pass
      // their own testIdPrefix (e.g. "accounting.agents.upload-csv-endorsement.form").
      uploadStatementForm: {
        statementDate: (prefix: string) => `${prefix}.input-statement-date` as const,
        file: (prefix: string) => `${prefix}.input-file` as const,
        bankAccount: (prefix: string) => `${prefix}.select-bank-account` as const,
        upload: (prefix: string) => `${prefix}.btn-upload` as const,
        verify: (prefix: string) => `${prefix}.btn-verify` as const,
        cancelVerification: (prefix: string) => `${prefix}.btn-cancel-verification` as const,
        vehicleLink: (prefix: string, index: number | string) => `${prefix}.link-vehicle-${index}` as const,
        affiliateLink: (prefix: string, index: number | string) => `${prefix}.link-affiliate-${index}` as const,
      },
    },
    credits: {
      filters: {
        allFilters: 'accounting.credits.filters.btn-all-filters',
        cancel: 'accounting.credits.filters.btn-cancel',
        clear: 'accounting.credits.filters.btn-clear',
        apply: 'accounting.credits.filters.btn-apply',
        clearAll: 'accounting.credits.filters.btn-clear-all',
      },
      table: {
        expand: (id: number | string) => `accounting.credits.table.btn-expand-${id}` as const,
        menuTrigger: (id: number | string) => `accounting.credits.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) => `accounting.credits.table.btn-${action}-${id}` as const,
      },
      expandedContent: {
        saleVehicle: (id: number | string) => `accounting.credits.expanded-content.btn-sale-vehicle-${id}` as const,
      },
    },
    instalments: {
      tabs: { tab: (key: string) => `accounting.instalments.tabs.tab-${key}` as const },
      manualEkycTransfer: {
        from: 'accounting.instalments.manual-ekyc-transfer.combobox-from',
        to: 'accounting.instalments.manual-ekyc-transfer.combobox-to',
        submit: 'accounting.instalments.manual-ekyc-transfer.btn-submit',
      },
      // ── Instalment loan (PRD-FIN-2026-003) ──────────────────────────────
      loanProvided: {
        filters: {
          search: 'accounting.instalments.loan-provided.filters.input-search',
          insurer: 'accounting.instalments.loan-provided.filters.select-insurer',
          writeOffCandidates: 'accounting.instalments.loan-provided.filters.select-write-off-candidates',
          allFilters: 'accounting.instalments.loan-provided.filters.btn-all-filters',
          cancel: 'accounting.instalments.loan-provided.filters.btn-cancel',
          clear: 'accounting.instalments.loan-provided.filters.btn-clear',
          apply: 'accounting.instalments.loan-provided.filters.btn-apply',
          clearAll: 'accounting.instalments.loan-provided.filters.btn-clear-all',
        },
        table: {
          row: (id: number | string) => `accounting.instalments.loan-provided.table.row-${id}` as const,
          debitNote: (id: number | string) =>
            `accounting.instalments.loan-provided.table.btn-debit-note-${id}` as const,
        },
      },
      debitNotes: {
        filters: {
          search: 'accounting.instalments.debit-notes.filters.input-search',
          status: 'accounting.instalments.debit-notes.filters.select-status',
          needsReview: 'accounting.instalments.debit-notes.filters.select-needs-review',
          insurer: 'accounting.instalments.debit-notes.filters.select-insurer',
          cancellationDate: 'accounting.instalments.debit-notes.filters.date-cancellation',
          allFilters: 'accounting.instalments.debit-notes.filters.btn-all-filters',
          cancel: 'accounting.instalments.debit-notes.filters.btn-cancel',
          clear: 'accounting.instalments.debit-notes.filters.btn-clear',
          apply: 'accounting.instalments.debit-notes.filters.btn-apply',
          clearAll: 'accounting.instalments.debit-notes.filters.btn-clear-all',
          export: 'accounting.instalments.debit-notes.filters.btn-export-csv',
        },
        ageing: {
          card: (key: string) => `accounting.instalments.debit-notes.ageing.readonly-card-${key}` as const,
          toggle: 'accounting.instalments.debit-notes.ageing.btn-toggle',
          collapsed: 'accounting.instalments.debit-notes.ageing.readonly-collapsed',
        },
        table: {
          row: (id: number | string) => `accounting.instalments.debit-notes.table.row-${id}` as const,
          expand: (id: number | string) => `accounting.instalments.debit-notes.table.btn-expand-${id}` as const,
          details: (id: number | string) => `accounting.instalments.debit-notes.table.btn-details-${id}` as const,
          needsReview: (id: number | string) =>
            `accounting.instalments.debit-notes.table.badge-needs-review-${id}` as const,
        },
        detailSheet: {
          hasClaim: 'accounting.instalments.debit-notes.detail-sheet.switch-has-claim',
          isTotalLoss: 'accounting.instalments.debit-notes.detail-sheet.switch-is-total-loss',
          note: 'accounting.instalments.debit-notes.detail-sheet.input-note',
          recalculate: 'accounting.instalments.debit-notes.detail-sheet.btn-recalculate',
          close: 'accounting.instalments.debit-notes.detail-sheet.btn-close',
          confirmSettled: 'accounting.instalments.debit-notes.detail-sheet.dialog-confirm-settled',
          writeOffAmount: 'accounting.instalments.debit-notes.detail-sheet.input-write-off-amount',
          writeOffReason: 'accounting.instalments.debit-notes.detail-sheet.input-write-off-reason',
          recordWriteOff: 'accounting.instalments.debit-notes.detail-sheet.btn-record-write-off',
        },
      },
      uploadLoan: {
        file: 'accounting.instalments.upload-loan.input-file',
        downloadTemplate: 'accounting.instalments.upload-loan.btn-download-template',
        preview: 'accounting.instalments.upload-loan.btn-preview',
        upload: 'accounting.instalments.upload-loan.btn-upload',
        reset: 'accounting.instalments.upload-loan.btn-reset',
        confirm: 'accounting.instalments.upload-loan.dialog-confirm',
        errorDialog: {
          close: 'accounting.instalments.upload-loan.error-dialog.btn-close',
        },
        previewTable: {
          row: (index: number | string) => `accounting.instalments.upload-loan.preview-table.row-${index}` as const,
        },
        historyTable: {
          // Celery task ids are UUID strings, not numbers.
          row: (id: string) => `accounting.instalments.upload-loan.history-table.row-${id}` as const,
        },
      },
    },
    insurerTables: {
      tabs: { tab: (key: string) => `accounting.insurer-tables.tabs.tab-${key}` as const },
      soaV1: {
        insurer: 'accounting.insurer-tables.soa-v1.select-insurer',
        statementDate: 'accounting.insurer-tables.soa-v1.input-statement-date',
        file: 'accounting.insurer-tables.soa-v1.input-file',
        upload: 'accounting.insurer-tables.soa-v1.btn-upload',
        backToForm: 'accounting.insurer-tables.soa-v1.btn-back-to-form',
        table: {
          expand: (index: number | string) => `accounting.insurer-tables.soa-v1.table.btn-expand-${index}` as const,
        },
        expandedContent: {
          saleVehicle: (index: number | string) =>
            `accounting.insurer-tables.soa-v1.expanded-content.btn-sale-vehicle-${index}` as const,
          endorsementVehicle: (id: number | string) =>
            `accounting.insurer-tables.soa-v1.expanded-content.btn-endorsement-vehicle-${id}` as const,
        },
      },
      soaResults: {
        filters: {
          allFilters: 'accounting.insurer-tables.soa-results.filters.btn-all-filters',
          cancel: 'accounting.insurer-tables.soa-results.filters.btn-cancel',
          clear: 'accounting.insurer-tables.soa-results.filters.btn-clear',
          apply: 'accounting.insurer-tables.soa-results.filters.btn-apply',
          clearAll: 'accounting.insurer-tables.soa-results.filters.btn-clear-all',
        },
        table: {
          menuTrigger: (id: number | string) =>
            `accounting.insurer-tables.soa-results.table.btn-menu-trigger-${id}` as const,
          action: (id: number | string, action: string) =>
            `accounting.insurer-tables.soa-results.table.btn-${action}-${id}` as const,
        },
        paymentModal: {
          bankAccount: 'accounting.insurer-tables.soa-results.payment-modal.select-bank-account',
          submit: 'accounting.insurer-tables.soa-results.payment-modal.btn-submit',
          cancel: 'accounting.insurer-tables.soa-results.payment-modal.btn-cancel',
        },
      },
    },
    uploadWht: {
      downloadSample: 'accounting.upload-wht.link-download-sample',
      file: 'accounting.upload-wht.input-file',
      upload: 'accounting.upload-wht.btn-upload',
    },
    // Reusable column/dialog components shared across agent tables (namespaced by caller's
    // own testIdPrefix + the row id).
    shared: {
      adjustInvoiceColumn: {
        adjust: (prefix: string, id: number | string) => `${prefix}.btn-adjust-invoice-${id}` as const,
      },
      agentCodeColumn: {
        link: (prefix: string, id: number | string) => `${prefix}.link-agent-code-${id}` as const,
      },
      vehicleColumn: {
        trigger: (prefix: string, id: number | string) => `${prefix}.btn-vehicle-${id}` as const,
      },
      recordPaymentDialog: {
        tab: (key: string) => `accounting.shared.record-payment-dialog.tab-${key}` as const,
        bankAccount: 'accounting.shared.record-payment-dialog.select-bank-account',
        bankReference: 'accounting.shared.record-payment-dialog.input-bank-reference',
        duplicateBankRefLink: 'accounting.shared.record-payment-dialog.link-duplicate-bank-ref',
        amountPaid: 'accounting.shared.record-payment-dialog.input-amount-paid',
        paidAt: 'accounting.shared.record-payment-dialog.input-paid-at',
        file: 'accounting.shared.record-payment-dialog.input-file',
        submit: 'accounting.shared.record-payment-dialog.btn-submit',
        paymentList: {
          delete: (id: number | string) =>
            `accounting.shared.record-payment-dialog.payment-list.btn-delete-${id}` as const,
          viewProof: (id: number | string) =>
            `accounting.shared.record-payment-dialog.payment-list.btn-view-proof-${id}` as const,
        },
      },
    },
  },

  // ---- gamification-admin module (Wave 5) ----
  // src/modules/gamification-admin/** — bulk redemption / non-motor points CSV upload,
  // mission creation form, mission inventory table
  gamificationAdmin: {
    tabs: { tab: (key: string) => `gamification-admin.tabs.tab-${key}` as const },
    bulkRedemption: {
      cancel: 'gamification-admin.bulk-redemption.btn-cancel',
      validate: 'gamification-admin.bulk-redemption.btn-validate',
      uploadAnother: 'gamification-admin.bulk-redemption.btn-upload-another',
      errorModal: {
        close: 'gamification-admin.bulk-redemption.error-modal.btn-close',
      },
    },
    nonMotorPoints: {
      cancel: 'gamification-admin.non-motor-points.btn-cancel',
      validate: 'gamification-admin.non-motor-points.btn-validate',
      uploadAnother: 'gamification-admin.non-motor-points.btn-upload-another',
    },
    missionCreation: {
      form: {
        missionName: 'gamification-admin.mission-creation.form.input-mission-name',
        campaignCode: 'gamification-admin.mission-creation.form.input-campaign-code',
        startDate: 'gamification-admin.mission-creation.form.btn-start-date',
        endDate: 'gamification-admin.mission-creation.form.btn-end-date',
        redemptionDeadline: 'gamification-admin.mission-creation.form.btn-redemption-deadline',
        eligibleAgentMlm: 'gamification-admin.mission-creation.form.checkbox-eligible-agent-mlm',
        eligibleAgentDirect: 'gamification-admin.mission-creation.form.checkbox-eligible-agent-direct',
        campaignTypeSinglePrize: 'gamification-admin.mission-creation.form.radio-campaign-type-single-prize',
        campaignTypeTieringPrize: 'gamification-admin.mission-creation.form.radio-campaign-type-tiering-prize',
        tierCountDecrement: 'gamification-admin.mission-creation.form.btn-tier-count-decrement',
        tierCountInput: 'gamification-admin.mission-creation.form.input-tier-count',
        tierCountIncrement: 'gamification-admin.mission-creation.form.btn-tier-count-increment',
        prizeRemove: (index: number) => `gamification-admin.mission-creation.form.btn-prize-remove-${index}` as const,
        prizeExistingReward: (index: number) =>
          `gamification-admin.mission-creation.form.select-prize-existing-reward-${index}` as const,
        prizeName: (index: number) => `gamification-admin.mission-creation.form.input-prize-name-${index}` as const,
        prizePointTarget: (index: number) =>
          `gamification-admin.mission-creation.form.input-prize-point-target-${index}` as const,
        prizeMlmLevel: (index: number) =>
          `gamification-admin.mission-creation.form.select-prize-mlm-level-${index}` as const,
        productEligibilityVmiCmi: 'gamification-admin.mission-creation.form.checkbox-product-eligibility-vmi-cmi',
        productEligibilitySoloCmi: 'gamification-admin.mission-creation.form.checkbox-product-eligibility-solo-cmi',
        productEligibilityNonMotor: 'gamification-admin.mission-creation.form.checkbox-product-eligibility-non-motor',
        pointConversionVmiCmi: 'gamification-admin.mission-creation.form.input-point-conversion-vmi-cmi',
        pointConversionSoloCmi: 'gamification-admin.mission-creation.form.input-point-conversion-solo-cmi',
        pointConversionNonMotor: 'gamification-admin.mission-creation.form.input-point-conversion-non-motor',
        categoryFilterTab: (key: string) =>
          `gamification-admin.mission-creation.form.tab-category-filter-${key}` as const,
        checkbox: (category: string, value: string) =>
          `gamification-admin.mission-creation.form.checkbox-${category}-${value}` as const,
        addRule: 'gamification-admin.mission-creation.form.btn-add-rule',
        ruleRemove: (ruleIndex: number) =>
          `gamification-admin.mission-creation.form.btn-rule-remove-${ruleIndex}` as const,
        ruleText: (ruleIndex: number) =>
          `gamification-admin.mission-creation.form.input-rule-text-${ruleIndex}` as const,
        ruleSpecial: (ruleIndex: number) =>
          `gamification-admin.mission-creation.form.checkbox-rule-special-${ruleIndex}` as const,
        childRuleText: (ruleIndex: number, childIndex: number) =>
          `gamification-admin.mission-creation.form.input-child-rule-text-${ruleIndex}-${childIndex}` as const,
        childRuleRemove: (ruleIndex: number, childIndex: number) =>
          `gamification-admin.mission-creation.form.btn-child-rule-remove-${ruleIndex}-${childIndex}` as const,
        addChildRule: (ruleIndex: number) =>
          `gamification-admin.mission-creation.form.btn-add-child-rule-${ruleIndex}` as const,
        reset: 'gamification-admin.mission-creation.form.btn-reset',
        submit: 'gamification-admin.mission-creation.form.btn-submit',
      },
    },
    missionInventory: {
      table: {
        row: (id: number | string) => `gamification-admin.mission-inventory.table.row-${id}` as const,
        expand: (id: number | string) => `gamification-admin.mission-inventory.table.btn-expand-${id}` as const,
        action: (id: number | string, action: string) =>
          `gamification-admin.mission-inventory.table.btn-${action}-${id}` as const,
      },
      deleteDialog: {
        cancel: 'gamification-admin.mission-inventory.delete-dialog.btn-cancel',
        confirm: 'gamification-admin.mission-inventory.delete-dialog.btn-confirm',
      },
    },
    // Reusable CSV upload flow + validation-results modal (namespaced by caller's testIdPrefix).
    shared: {
      csvUpload: {
        fileInput: (prefix: string) => `${prefix}.input-file` as const,
        select: (prefix: string) => `${prefix}.btn-select` as const,
        clear: (prefix: string) => `${prefix}.btn-clear` as const,
        download: (prefix: string) => `${prefix}.btn-download` as const,
      },
      validationResultsModal: {
        cancel: (prefix: string) => `${prefix}.btn-cancel` as const,
        confirm: (prefix: string) => `${prefix}.btn-confirm` as const,
      },
    },
  },

  // ---- staff-management module (Wave 5) ----
  // src/modules/staff-management/** — bulk operations, leaves, roles, team-list, timing
  staffManagement: {
    tabs: { tab: (key: string) => `staff-management.tabs.tab-${key}` as const },
    bulkOperations: {
      form: {
        fileInput: 'staff-management.bulk-operations.form.input-file',
        removeFile: 'staff-management.bulk-operations.form.btn-remove-file',
        submit: 'staff-management.bulk-operations.form.btn-submit',
      },
      results: {
        goBack: 'staff-management.bulk-operations.results.btn-go-back',
      },
      table: {
        row: (id: number | string) => `staff-management.bulk-operations.table.row-${id}` as const,
      },
    },
    leaves: {
      filters: { search: 'staff-management.leaves.filters.input-search' },
      header: {
        addLeave: 'staff-management.leaves.header.btn-add-leave',
        bulkUpload: 'staff-management.leaves.header.btn-bulk-upload',
      },
      table: {
        row: (id: number | string) => `staff-management.leaves.table.row-${id}` as const,
        menuTrigger: (id: number | string) => `staff-management.leaves.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) => `staff-management.leaves.table.btn-${action}-${id}` as const,
      },
      bulkLeaveUpload: {
        fileInput: 'staff-management.leaves.bulk-leave-upload.input-file',
        cancel: 'staff-management.leaves.bulk-leave-upload.btn-cancel',
        submit: 'staff-management.leaves.bulk-leave-upload.btn-submit',
      },
      createLeaveDialog: {
        staffSelect: 'staff-management.leaves.create-leave-dialog.select-staff',
        startDate: 'staff-management.leaves.create-leave-dialog.btn-start-date',
        startTime: 'staff-management.leaves.create-leave-dialog.input-start-time',
        endDate: 'staff-management.leaves.create-leave-dialog.btn-end-date',
        endTime: 'staff-management.leaves.create-leave-dialog.input-end-time',
        cancel: 'staff-management.leaves.create-leave-dialog.btn-cancel',
        submit: 'staff-management.leaves.create-leave-dialog.btn-submit',
      },
    },
    roles: {
      header: { assignRoleTrigger: 'staff-management.roles.header.btn-assign-role-trigger' },
      table: {
        row: (id: number | string) => `staff-management.roles.table.row-${id}` as const,
        action: (id: number | string, action: string) => `staff-management.roles.table.btn-${action}-${id}` as const,
      },
      revokeConfirm: {
        cancel: 'staff-management.roles.revoke-confirm.btn-cancel',
        confirm: 'staff-management.roles.revoke-confirm.btn-confirm',
      },
      assignRoleDialog: {
        roleSelect: 'staff-management.roles.assign-role-dialog.select-role',
        nickname: 'staff-management.roles.assign-role-dialog.input-nickname',
        cancel: 'staff-management.roles.assign-role-dialog.btn-cancel',
        submit: 'staff-management.roles.assign-role-dialog.btn-submit',
      },
      userSearchInput: 'staff-management.roles.input-user-search',
    },
    teamList: {
      header: { addTeam: 'staff-management.team-list.header.btn-add-team' },
      table: {
        row: (id: number | string) => `staff-management.team-list.table.row-${id}` as const,
        action: (id: number | string, action: string) =>
          `staff-management.team-list.table.btn-${action}-${id}` as const,
      },
      deleteConfirm: {
        cancel: 'staff-management.team-list.delete-confirm.btn-cancel',
        confirm: 'staff-management.team-list.delete-confirm.btn-confirm',
      },
      addTeamModal: {
        form: {
          field: (name: string) => `staff-management.team-list.add-team-modal.form.input-${name}` as const,
          select: (name: string) => `staff-management.team-list.add-team-modal.form.select-${name}` as const,
          cancel: 'staff-management.team-list.add-team-modal.form.btn-cancel',
          submit: 'staff-management.team-list.add-team-modal.form.btn-submit',
        },
      },
    },
    timing: {
      header: {
        addTeam: 'staff-management.timing.header.btn-add-team',
        addTiming: 'staff-management.timing.header.btn-add-timing',
      },
      table: {
        row: (id: number | string) => `staff-management.timing.table.row-${id}` as const,
        action: (id: number | string, action: string) => `staff-management.timing.table.btn-${action}-${id}` as const,
        teamDropdown: (id: number | string) => `staff-management.timing.table.dropdown-team-${id}` as const,
      },
      deleteConfirm: {
        cancel: 'staff-management.timing.delete-confirm.btn-cancel',
        confirm: 'staff-management.timing.delete-confirm.btn-confirm',
      },
      addTeamDialog: {
        field: (name: string) => `staff-management.timing.add-team-dialog.input-${name}` as const,
        cancel: 'staff-management.timing.add-team-dialog.btn-cancel',
        submit: 'staff-management.timing.add-team-dialog.btn-submit',
      },
      createTimingDialog: {
        staffSelect: 'staff-management.timing.create-timing-dialog.select-staff',
        startTime: 'staff-management.timing.create-timing-dialog.input-start-time',
        endTime: 'staff-management.timing.create-timing-dialog.input-end-time',
        secondShiftToggle: 'staff-management.timing.create-timing-dialog.toggle-second-shift',
        secondShiftStartTime: 'staff-management.timing.create-timing-dialog.input-second-shift-start-time',
        secondShiftEndTime: 'staff-management.timing.create-timing-dialog.input-second-shift-end-time',
        cancel: 'staff-management.timing.create-timing-dialog.btn-cancel',
        submit: 'staff-management.timing.create-timing-dialog.btn-submit',
      },
      editTimingDialog: {
        startTime: 'staff-management.timing.edit-timing-dialog.input-start-time',
        endTime: 'staff-management.timing.edit-timing-dialog.input-end-time',
        secondShiftToggle: 'staff-management.timing.edit-timing-dialog.toggle-second-shift',
        secondShiftStartTime: 'staff-management.timing.edit-timing-dialog.input-second-shift-start-time',
        secondShiftEndTime: 'staff-management.timing.edit-timing-dialog.input-second-shift-end-time',
        cancel: 'staff-management.timing.edit-timing-dialog.btn-cancel',
        submit: 'staff-management.timing.edit-timing-dialog.btn-submit',
      },
    },
  },

  // ---- notifications module (Wave 5) ----
  // src/modules/notifications/** — tabbed shell (list, history, configs, templates, broadcast)
  notificationAdmin: {
    tabs: { tab: (key: string) => `notification-admin.tabs.tab-${key}` as const },
    list: {
      tabs: { tab: (key: string) => `notification-admin.list.tabs.tab-${key}` as const },
      filters: { unreadOnly: 'notification-admin.list.filters.toggle-unread-only' },
      table: {
        row: (id: number | string) => `notification-admin.list.table.row-${id}` as const,
        prevPage: 'notification-admin.list.table.btn-prev-page',
        nextPage: 'notification-admin.list.table.btn-next-page',
      },
    },
    history: {
      table: {
        row: (id: number | string) => `notification-admin.history.table.row-${id}` as const,
        prevPage: 'notification-admin.history.table.btn-prev-page',
        nextPage: 'notification-admin.history.table.btn-next-page',
      },
    },
    configs: {
      btnNew: 'notification-admin.configs.btn-new',
      table: {
        row: (id: number | string) => `notification-admin.configs.table.row-${id}` as const,
        prevPage: 'notification-admin.configs.table.btn-prev-page',
        nextPage: 'notification-admin.configs.table.btn-next-page',
        action: (id: number | string, action: string) =>
          `notification-admin.configs.table.btn-${action}-${id}` as const,
      },
      deleteDialog: {
        cancel: 'notification-admin.configs.delete-dialog.btn-cancel',
        confirm: 'notification-admin.configs.delete-dialog.btn-confirm',
      },
      form: {
        field: (name: string) => `notification-admin.configs.form.input-${name}` as const,
        select: (name: string) => `notification-admin.configs.form.select-${name}` as const,
        channelCheckbox: (channel: string) => `notification-admin.configs.form.checkbox-channel-${channel}` as const,
        audienceCheckbox: (audience: string) =>
          `notification-admin.configs.form.checkbox-audience-${audience}` as const,
        isActive: 'notification-admin.configs.form.toggle-is-active',
        cancel: 'notification-admin.configs.form.btn-cancel',
        submit: 'notification-admin.configs.form.btn-submit',
      },
    },
    templates: {
      btnNew: 'notification-admin.templates.btn-new',
      table: {
        row: (id: number | string) => `notification-admin.templates.table.row-${id}` as const,
        prevPage: 'notification-admin.templates.table.btn-prev-page',
        nextPage: 'notification-admin.templates.table.btn-next-page',
        action: (id: number | string, action: string) =>
          `notification-admin.templates.table.btn-${action}-${id}` as const,
      },
      deleteDialog: {
        cancel: 'notification-admin.templates.delete-dialog.btn-cancel',
        confirm: 'notification-admin.templates.delete-dialog.btn-confirm',
      },
      form: {
        field: (name: string) => `notification-admin.templates.form.input-${name}` as const,
        select: (name: string) => `notification-admin.templates.form.select-${name}` as const,
        isActive: 'notification-admin.templates.form.toggle-is-active',
        cancel: 'notification-admin.templates.form.btn-cancel',
        submit: 'notification-admin.templates.form.btn-submit',
      },
    },
    broadcast: {
      form: {
        field: (name: string) => `notification-admin.broadcast.form.input-${name}` as const,
        select: (name: string) => `notification-admin.broadcast.form.select-${name}` as const,
        agentTypeCheckbox: (option: string) =>
          `notification-admin.broadcast.form.checkbox-agent-type-${option}` as const,
        channelCheckbox: (channel: string) => `notification-admin.broadcast.form.checkbox-channel-${channel}` as const,
        silent: 'notification-admin.broadcast.form.toggle-silent',
        clear: 'notification-admin.broadcast.form.btn-clear',
        submit: 'notification-admin.broadcast.form.btn-submit',
      },
    },
  },

  // ---- assignment-config module (Wave 5) ----
  // src/modules/assignment-config/** — tabbed shell (config rules, rework reasons)
  assignmentConfig: {
    tabs: { tab: (key: string) => `assignment-config.tabs.tab-${key}` as const },
    header: { addConfig: 'assignment-config.header.btn-add-config' },
    filters: { type: 'assignment-config.filters.select-type' },
    table: {
      row: (id: number | string) => `assignment-config.table.row-${id}` as const,
      action: (id: number | string, action: string) => `assignment-config.table.btn-${action}-${id}` as const,
    },
    deleteConfigDialog: {
      cancel: 'assignment-config.delete-config-dialog.btn-cancel',
      confirm: 'assignment-config.delete-config-dialog.btn-confirm',
    },
    form: {
      field: (name: string) => `assignment-config.form.input-${name}` as const,
      select: (name: string) => `assignment-config.form.select-${name}` as const,
      checkbox: (name: string) => `assignment-config.form.checkbox-${name}` as const,
      toggle: (name: string) => `assignment-config.form.toggle-${name}` as const,
      cancel: 'assignment-config.form.btn-cancel',
      submit: 'assignment-config.form.btn-submit',
    },
    reworkReasons: {
      header: { addReason: 'assignment-config.rework-reasons.header.btn-add-reason' },
      filters: { party: 'assignment-config.rework-reasons.filters.select-party' },
      table: {
        row: (id: number | string) => `assignment-config.rework-reasons.table.row-${id}` as const,
        action: (id: number | string, action: string) =>
          `assignment-config.rework-reasons.table.btn-${action}-${id}` as const,
      },
      deleteDialog: {
        cancel: 'assignment-config.rework-reasons.delete-dialog.btn-cancel',
        confirm: 'assignment-config.rework-reasons.delete-dialog.btn-confirm',
      },
      form: {
        field: (name: string) => `assignment-config.rework-reasons.form.input-${name}` as const,
        select: (name: string) => `assignment-config.rework-reasons.form.select-${name}` as const,
        checkbox: (name: string) => `assignment-config.rework-reasons.form.checkbox-${name}` as const,
        toggle: (name: string) => `assignment-config.rework-reasons.form.toggle-${name}` as const,
        cancel: 'assignment-config.rework-reasons.form.btn-cancel',
        submit: 'assignment-config.rework-reasons.form.btn-submit',
      },
    },
  },

  // ---- bannerUpload module (Wave 5) ----
  // src/modules/bannerUpload/** — tabbed shell (highlight upload, commission/promotion
  // highlights tables, announcement upload/inventory)
  bannerUpload: {
    tabs: { tab: (key: string) => `banner-upload.tabs.tab-${key}` as const },
    highlightUpload: {
      form: {
        agentType: 'banner-upload.highlight-upload.form.select-agent-type',
        bannerType: 'banner-upload.highlight-upload.form.select-banner-type',
        weight: 'banner-upload.highlight-upload.form.select-weight',
        redirectionLink: 'banner-upload.highlight-upload.form.input-redirection-link',
        validFrom: 'banner-upload.highlight-upload.form.input-valid-from',
        validTill: 'banner-upload.highlight-upload.form.input-valid-till',
        submit: 'banner-upload.highlight-upload.form.btn-submit',
      },
    },
    commissionHighlights: {
      table: {
        view: (id: number | string) => `banner-upload.commission-highlights.table.btn-view-${id}` as const,
        menuTrigger: (id: number | string) =>
          `banner-upload.commission-highlights.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) =>
          `banner-upload.commission-highlights.table.btn-${action}-${id}` as const,
      },
    },
    promotionHighlights: {
      table: {
        view: (id: number | string) => `banner-upload.promotion-highlights.table.btn-view-${id}` as const,
        menuTrigger: (id: number | string) =>
          `banner-upload.promotion-highlights.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) =>
          `banner-upload.promotion-highlights.table.btn-${action}-${id}` as const,
      },
    },
    announcementUpload: {
      richTextEditor: 'banner-upload.announcement-upload.textarea-rich-text-editor',
      form: {
        announcementType: 'banner-upload.announcement-upload.form.select-announcement-type',
        agentType: (type: string) => `banner-upload.announcement-upload.form.checkbox-agent-type-${type}` as const,
        title: 'banner-upload.announcement-upload.form.input-title',
        fromTime: 'banner-upload.announcement-upload.form.select-from-time',
        toTime: 'banner-upload.announcement-upload.form.select-to-time',
        validFrom: 'banner-upload.announcement-upload.form.input-valid-from',
        validTill: 'banner-upload.announcement-upload.form.input-valid-till',
        reset: 'banner-upload.announcement-upload.form.btn-reset',
        submit: 'banner-upload.announcement-upload.form.btn-submit',
      },
    },
    announcementInventory: {
      editDialog: {
        contentEditor: 'banner-upload.announcement-inventory.edit-dialog.textarea-content-editor',
        cancel: 'banner-upload.announcement-inventory.edit-dialog.btn-cancel',
        update: 'banner-upload.announcement-inventory.edit-dialog.btn-update',
      },
      // Shared row-actions dropdown (namespaced by caller's testIdPrefix + row id).
      sharedTable: {
        menuTrigger: (prefix: string, id: number | string) => `${prefix}.btn-menu-trigger-${id}` as const,
        action: (prefix: string, id: number | string, action: string) => `${prefix}.btn-${action}-${id}` as const,
      },
    },
  },

  // ---- profile module (Wave 5) ----
  // src/modules/profile/** — own-profile page (header/photo, sections, address, contact
  // info, change-phone modal, subagents)
  profile: {
    header: {
      uploadPhotoInput: 'profile.header.input-upload-photo',
      uploadPhotoBtn: 'profile.header.btn-upload-photo',
      switchAccount: 'profile.header.btn-switch-account',
    },
    page: { logout: 'profile.page.btn-logout' },
    sections: {
      toggle: (key: string) => `profile.sections.btn-toggle-${key}` as const,
      addressEditBtn: 'profile.sections.btn-address-edit',
      personalReferralReadonly: 'profile.sections.readonly-personal-referral',
      brokerLicense: {
        fileInput: 'profile.sections.broker-license.input-file',
        chooseFileBtn: 'profile.sections.broker-license.btn-choose-file',
        viewDoc: (id: number | string) => `profile.sections.broker-license.btn-view-doc-${id}` as const,
        deleteDoc: (id: number | string) => `profile.sections.broker-license.btn-delete-doc-${id}` as const,
      },
    },
    subagent: { addBtn: 'profile.subagent.btn-add' },
    editAddressSheet: {
      firstLineInput: 'profile.edit-address-sheet.input-first-line',
      postalCodeReadonly: 'profile.edit-address-sheet.readonly-postal-code',
      cancelBtn: 'profile.edit-address-sheet.btn-cancel',
      saveBtn: 'profile.edit-address-sheet.btn-save',
    },
    contactInfo: {
      changePhone: 'profile.contact-info.link-change-phone',
      editCommEmail: 'profile.contact-info.link-edit-comm-email',
      commEmailInput: 'profile.contact-info.input-comm-email',
      commEmailCancel: 'profile.contact-info.btn-comm-email-cancel',
      commEmailSave: 'profile.contact-info.btn-comm-email-save',
      verifyEmail: 'profile.contact-info.link-verify-email',
    },
    changePhone: {
      phoneInput: 'profile.change-phone.input-phone',
      sendOtp: 'profile.change-phone.btn-send-otp',
      otpCode: (index: number) => `profile.change-phone.input-otp-code-${index}` as const,
      resendOtp: 'profile.change-phone.btn-resend-otp',
    },
    // Reusable multi-select editable field (namespaced by the caller's fieldKey).
    editableField: {
      field: (fieldKey: string) => `profile.editable-field.btn-${fieldKey}` as const,
      checkboxOption: (fieldKey: string, value: string) =>
        `profile.editable-field.checkbox-${fieldKey}-${value}` as const,
      cancel: (fieldKey: string) => `profile.editable-field.btn-cancel-${fieldKey}` as const,
      confirm: (fieldKey: string) => `profile.editable-field.btn-confirm-${fieldKey}` as const,
    },
  },

  // ---- event-calendar module (Wave 5) ----
  // src/modules/event-calendar/** — events table + create/edit form
  eventCalendar: {
    header: { create: 'event-calendar.header.btn-create' },
    filters: {
      search: 'event-calendar.filters.input-search',
      clear: 'event-calendar.filters.btn-clear',
    },
    table: {
      row: (id: number | string) => `event-calendar.table.row-${id}` as const,
      expand: (id: number | string) => `event-calendar.table.btn-expand-${id}` as const,
      action: (id: number | string, action: string) => `event-calendar.table.btn-${action}-${id}` as const,
      link: (kind: string, id: number | string) => `event-calendar.table.link-${kind}-${id}` as const,
    },
    deleteDialog: {
      cancel: 'event-calendar.delete-dialog.btn-cancel',
      confirm: 'event-calendar.delete-dialog.btn-confirm',
    },
    form: {
      field: (name: string) => `event-calendar.form.input-${name}` as const,
      checkbox: (type: string) => `event-calendar.form.checkbox-account-type-${type}` as const,
      dateTimePicker: {
        date: (which: string) => `event-calendar.form.btn-${which}-date` as const,
        time: (which: string) => `event-calendar.form.btn-${which}-time` as const,
        hour: (which: string) => `event-calendar.form.select-${which}-hour` as const,
        minute: (which: string) => `event-calendar.form.select-${which}-minute` as const,
      },
      cancel: 'event-calendar.form.btn-cancel',
      submit: 'event-calendar.form.btn-submit',
    },
  },

  // ---- insurer-settings module (Wave 5) ----
  // src/modules/insurer-settings/** — tabbed shell (insurer email, insurer toggles,
  // non-motor insurer email)
  insurerSettings: {
    tabs: { tab: (key: string) => `insurer-settings.tabs.tab-${key}` as const },
    insurerEmail: {
      table: {
        row: (id: number | string) => `insurer-settings.insurer-email.table.row-${id}` as const,
        menuTrigger: (id: number | string) => `insurer-settings.insurer-email.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) =>
          `insurer-settings.insurer-email.table.btn-${action}-${id}` as const,
      },
      editInsurerEmailForm: {
        select: (name: string) => `insurer-settings.insurer-email.edit-insurer-email-form.select-${name}` as const,
        field: (name: string) => `insurer-settings.insurer-email.edit-insurer-email-form.input-${name}` as const,
        cancel: 'insurer-settings.insurer-email.edit-insurer-email-form.btn-cancel',
        submit: 'insurer-settings.insurer-email.edit-insurer-email-form.btn-submit',
      },
    },
    insurerToggles: {
      table: {
        row: (id: number | string) => `insurer-settings.insurer-toggles.table.row-${id}` as const,
        toggle: (id: number | string, field: string) =>
          `insurer-settings.insurer-toggles.table.toggle-${field}-${id}` as const,
      },
    },
    // Cancellation refund-rule matrix (PRD-FIN-2026-003).
    refundRules: {
      insurer: 'insurer-settings.refund-rules.combobox-insurer',
      includeInactive: 'insurer-settings.refund-rules.switch-include-inactive',
      addRule: 'insurer-settings.refund-rules.btn-add-rule',
      table: {
        row: (id: number | string) => `insurer-settings.refund-rules.table.row-${id}` as const,
        action: (id: number | string, action: string) =>
          `insurer-settings.refund-rules.table.btn-${action}-${id}` as const,
      },
      ruleForm: {
        select: (name: string) => `insurer-settings.refund-rules.rule-form.select-${name}` as const,
        field: (name: string) => `insurer-settings.refund-rules.rule-form.input-${name}` as const,
        date: (name: string) => `insurer-settings.refund-rules.rule-form.date-${name}` as const,
        toggle: (name: string) => `insurer-settings.refund-rules.rule-form.switch-${name}` as const,
        cancel: 'insurer-settings.refund-rules.rule-form.btn-cancel',
        submit: 'insurer-settings.refund-rules.rule-form.btn-submit',
      },
      confirmDeactivate: 'insurer-settings.refund-rules.dialog-confirm-deactivate',
    },
    nonMotorInsurerEmail: {
      table: {
        row: (id: number | string) => `insurer-settings.non-motor-insurer-email.table.row-${id}` as const,
        menuTrigger: (id: number | string) =>
          `insurer-settings.non-motor-insurer-email.table.btn-menu-trigger-${id}` as const,
        action: (id: number | string, action: string) =>
          `insurer-settings.non-motor-insurer-email.table.btn-${action}-${id}` as const,
      },
      editInsurerEmailModal: {
        select: (name: string) =>
          `insurer-settings.non-motor-insurer-email.edit-insurer-email-modal.select-${name}` as const,
        radio: (name: string) =>
          `insurer-settings.non-motor-insurer-email.edit-insurer-email-modal.radio-${name}` as const,
        field: (name: string) =>
          `insurer-settings.non-motor-insurer-email.edit-insurer-email-modal.input-${name}` as const,
        cancel: 'insurer-settings.non-motor-insurer-email.edit-insurer-email-modal.btn-cancel',
        submit: 'insurer-settings.non-motor-insurer-email.edit-insurer-email-modal.btn-submit',
      },
    },
  },

  // ---- price-list-admin module (Wave 5) ----
  // src/modules/price-list-admin/** — tabbed shell (file upload, modify)
  priceListAdmin: {
    tabs: { tab: (key: string) => `price-list-admin.tabs.tab-${key}` as const },
    fileupload: {
      download: 'price-list-admin.fileupload.btn-download',
      appName: 'price-list-admin.fileupload.select-app-name',
      functionName: 'price-list-admin.fileupload.select-function-name',
      argument: (argName: string) => `price-list-admin.fileupload.select-argument-${argName}` as const,
      uploading: 'price-list-admin.fileupload.btn-uploading',
      confirmDialog: {
        cancel: 'price-list-admin.fileupload.confirm-dialog.btn-cancel',
        proceed: 'price-list-admin.fileupload.confirm-dialog.btn-proceed',
      },
      errorDialog: {
        cancel: 'price-list-admin.fileupload.error-dialog.btn-cancel',
        continue: 'price-list-admin.fileupload.error-dialog.btn-continue',
        close: 'price-list-admin.fileupload.error-dialog.btn-close',
      },
    },
    modify: {
      insuranceClass: 'price-list-admin.modify.select-insurance-class',
      bikeToggle: 'price-list-admin.modify.toggle-bike',
      upload: 'price-list-admin.modify.btn-upload',
      delete: 'price-list-admin.modify.btn-delete',
    },
  },

  // ---- holidays module (Wave 5) ----
  // src/modules/holidays/** — list + create/edit form
  holidays: {
    header: { add: 'holidays.header.btn-add' },
    filters: {
      type: 'holidays.filters.select-type',
      year: 'holidays.filters.select-year',
      reset: 'holidays.filters.btn-reset',
    },
    list: {
      table: {
        row: (id: number | string) => `holidays.list.table.row-${id}` as const,
        action: (id: number | string, action: string) => `holidays.list.table.btn-${action}-${id}` as const,
      },
    },
    form: {
      field: (name: string) => `holidays.form.input-${name}` as const,
      select: (name: string) => `holidays.form.select-${name}` as const,
      cancel: 'holidays.form.btn-cancel',
      submit: 'holidays.form.btn-submit',
    },
  },

  // ---- promotion-admin module (Wave 5) ----
  // src/modules/promotion-admin/** — campaign promotion (tabs, upload, inventory), package promotion
  promotionAdmin: {
    packagePromotion: { download: 'promotion-admin.package-promotion.btn-download' },
    campaignPromotion: {
      tabs: { tab: (key: string) => `promotion-admin.campaign-promotion.tabs.tab-${key}` as const },
      upload: {
        richTextEditor: 'promotion-admin.campaign-promotion.upload.textarea-rich-text-editor',
        form: {
          title: 'promotion-admin.campaign-promotion.upload.form.input-title',
          agentType: (type: string) =>
            `promotion-admin.campaign-promotion.upload.form.checkbox-agent-type-${type}` as const,
          reset: 'promotion-admin.campaign-promotion.upload.form.btn-reset',
          submit: 'promotion-admin.campaign-promotion.upload.form.btn-submit',
        },
      },
      inventory: {
        editDialog: {
          contentEditor: 'promotion-admin.campaign-promotion.inventory.edit-dialog.textarea-content-editor',
          cancel: 'promotion-admin.campaign-promotion.inventory.edit-dialog.btn-cancel',
          update: 'promotion-admin.campaign-promotion.inventory.edit-dialog.btn-update',
        },
      },
    },
  },

  // ---- policy-admin module (Wave 5) ----
  // src/modules/policy-admin/** — tabbed shell (policy tracking, insurer tracking)
  policyAdmin: {
    tabs: { tab: (key: string) => `policy-admin.tabs.tab-${key}` as const },
    policyTracking: { upload: 'policy-admin.policy-tracking.btn-upload' },
    cancellationTracking: { upload: 'policy-admin.cancellation-tracking.btn-upload' },
    // Reusable file-upload section (namespaced by caller's testIdPrefix).
    fileUploadSection: {
      fileInput: (prefix: string) => `${prefix}.input-file` as const,
      chooseFile: (prefix: string) => `${prefix}.btn-choose-file` as const,
      remove: (prefix: string) => `${prefix}.btn-remove` as const,
    },
  },

  // ---- staff-for-tasks module (Wave 5) ----
  // src/modules/staff-for-tasks/** — staff-to-task assignment cards + add modal
  staffForTasks: {
    header: { add: 'staff-for-tasks.header.btn-add' },
    card: { remove: (id: number | string) => `staff-for-tasks.card.btn-remove-${id}` as const },
    form: {
      staff: 'staff-for-tasks.form.combobox-staff',
      task: 'staff-for-tasks.form.combobox-task',
      cancel: 'staff-for-tasks.form.btn-cancel',
      submit: 'staff-for-tasks.form.btn-submit',
    },
  },

  // ---- url-shortener module (Wave 5) ----
  // src/modules/url-shortener/** — generate short url form
  shortenedUrl: {
    form: {
      field: (name: string) => `shortened-url.form.input-${name}` as const,
      submit: 'shortened-url.form.btn-submit',
    },
    result: { copy: 'shortened-url.result.btn-copy' },
  },

  // ---- historical-report-requests module (Wave 5) ----
  // src/modules/historical-report-requests/** — affiliate filter + report cards
  historicalReportRequests: {
    filters: {
      affiliate: 'historical-report-requests.filters.select-affiliate',
      affiliateSearch: 'historical-report-requests.filters.input-affiliate-search',
    },
    reportCard: {
      download: (id: number | string) => `historical-report-requests.report-card.btn-download-${id}` as const,
    },
  },

  // ---- main navigation (Wave 5) ----
  nav: {
    header: {
      logo: 'nav.header.link-logo',
      searchExpand: 'nav.header.btn-search-expand',
      search: 'nav.header.input-search',
      profileTrigger: 'nav.header.btn-profile-trigger',
      profileMenu: 'nav.header.menu-profile',
      logout: 'nav.header.btn-logout',
      languageTrigger: 'nav.header.btn-language-trigger',
      languageOption: (lang: string) => `nav.header.menu-language-${lang}` as const,
    },
    sidebar: {
      link: (route: string) => `nav.sidebar.link-${route}` as const,
      dropdownToggle: (key: string) => `nav.sidebar.btn-dropdown-${key}` as const,
    },
  },

  // ---- form-shared-resources reusable components (namespaced by caller's prefix) ----
  formShared: {
    autoOpenSelect: (prefix: string) => `${prefix}.combobox-select` as const,
    carTypeCard: (prefix: string, groupKey: string) => `${prefix}.btn-${groupKey.replace(/_/g, '-')}` as const,
    radio: (prefix: string, name: string) => `${prefix}.radio-${name}` as const,
    checkboxGroupAll: (prefix: string) => `${prefix}.checkbox-all` as const,
    checkboxGroup: (prefix: string, optionId: string) => `${prefix}.checkbox-${optionId}` as const,
  },

  // ---- shared component generators (namespaced by caller's prefix) ----
  filters: {
    search: (prefix: string) => `${prefix}.input-search` as const,
    select: (prefix: string) => `${prefix}.select-filter` as const,
    dateRange: (prefix: string) => `${prefix}.input-date-range` as const,
    numberRange: (prefix: string, bound: 'min' | 'max') => `${prefix}.input-${bound}` as const,
  },
  // Notification tray + sidebar bell (src/modules/shared/notifications). This surface post-dates
  // the original registry, so its ids live here rather than under a feature page.
  nonMotorPolicies: {
    expanded: {
      customerPhone: 'non-motor-policies.expanded.link-customer-phone',
    },
  },
  notificationsTray: {
    bell: 'shared.notifications.btn-bell',
    connectionStatus: 'shared.notifications.btn-connection-status',
    reconnect: 'shared.notifications.btn-reconnect',
    markAllRead: 'shared.notifications.btn-mark-all-read',
    goToAdmin: 'shared.notifications.btn-go-to-admin',
    tab: (name: string) => `shared.notifications.tab-${name}` as const,
    unreadOnly: 'shared.notifications.switch-unread-only',
    item: (id: number | string) => `shared.notifications.btn-item-${id}` as const,
  },

  // Generic table blocks (EmptyState / ErrorState / SortableHeader). Like `pagination`, these are
  // reused by every module's table, so the caller supplies the page-section prefix.
  tableBlocks: {
    emptyAction: (prefix: string) => `${prefix}.btn-empty-action` as const,
    retry: (prefix: string) => `${prefix}.btn-retry` as const,
    sortHeader: (prefix: string, column: string) => `${prefix}.btn-sort-${column}` as const,
  },

  // Global search overlay (src/modules/global-search).
  globalSearch: {
    collapsedTrigger: 'global-search.btn-collapsed-trigger',
    input: 'global-search.input-search',
    clear: 'global-search.btn-clear',
    overlay: 'global-search.overlay',
    panel: 'global-search.panel',
    loading: 'global-search.badge-loading',
    close: 'global-search.btn-close',
  },

  // Bike prequote form (src/modules/prequote-forms/Bike).
  prequoteBike: {
    getQuote: 'prequote-bike.btn-get-quote',
    details: 'prequote-bike.details',
  },

  opsDashboard: {
    // One section per feature area under src/modules/ops-dashboard, mirroring the folder layout.
    // Each takes a name so ids are derived from the control's own field key rather than hand-typed.
    invoices: {
      field: (name: string) => `ops-dashboard.invoices.input-${name}` as const,
      select: (name: string) => `ops-dashboard.invoices.select-${name}` as const,
      date: (name: string) => `ops-dashboard.invoices.date-${name}` as const,
      toggle: (name: string) => `ops-dashboard.invoices.toggle-${name}` as const,
      action: (name: string) => `ops-dashboard.invoices.btn-${name}` as const,
      row: (id: number | string) => `ops-dashboard.invoices.row-${id}` as const,
      rowAction: (id: number | string, action: string) => `ops-dashboard.invoices.btn-${action}-${id}` as const,
    },
    policyManagement: {
      field: (name: string) => `ops-dashboard.policy-management.input-${name}` as const,
      select: (name: string) => `ops-dashboard.policy-management.select-${name}` as const,
      date: (name: string) => `ops-dashboard.policy-management.date-${name}` as const,
      toggle: (name: string) => `ops-dashboard.policy-management.toggle-${name}` as const,
      action: (name: string) => `ops-dashboard.policy-management.btn-${name}` as const,
    },
    createdBy: {
      field: (name: string) => `ops-dashboard.created-by.input-${name}` as const,
      select: (name: string) => `ops-dashboard.created-by.select-${name}` as const,
      date: (name: string) => `ops-dashboard.created-by.date-${name}` as const,
      action: (name: string) => `ops-dashboard.created-by.btn-${name}` as const,
    },
    credit: {
      field: (name: string) => `ops-dashboard.credit.input-${name}` as const,
      select: (name: string) => `ops-dashboard.credit.select-${name}` as const,
      action: (name: string) => `ops-dashboard.credit.btn-${name}` as const,
    },
    commission: {
      field: (name: string) => `ops-dashboard.commission.input-${name}` as const,
      select: (name: string) => `ops-dashboard.commission.select-${name}` as const,
      action: (name: string) => `ops-dashboard.commission.btn-${name}` as const,
    },
    ekyc: {
      field: (name: string) => `ops-dashboard.ekyc.input-${name}` as const,
      select: (name: string) => `ops-dashboard.ekyc.select-${name}` as const,
      action: (name: string) => `ops-dashboard.ekyc.btn-${name}` as const,
    },
    email: {
      field: (name: string) => `ops-dashboard.email.input-${name}` as const,
      select: (name: string) => `ops-dashboard.email.select-${name}` as const,
      action: (name: string) => `ops-dashboard.email.btn-${name}` as const,
    },
    endorsement: {
      field: (name: string) => `ops-dashboard.endorsement.input-${name}` as const,
      select: (name: string) => `ops-dashboard.endorsement.select-${name}` as const,
      action: (name: string) => `ops-dashboard.endorsement.btn-${name}` as const,
    },
    // The three quotation steps (steps/step1..3).
    step: {
      field: (step: string, name: string) => `ops-dashboard.${step}.input-${name}` as const,
      select: (step: string, name: string) => `ops-dashboard.${step}.select-${name}` as const,
      date: (step: string, name: string) => `ops-dashboard.${step}.date-${name}` as const,
      toggle: (step: string, name: string) => `ops-dashboard.${step}.toggle-${name}` as const,
      radio: (step: string, name: string) => `ops-dashboard.${step}.radio-${name}` as const,
      action: (step: string, name: string) => `ops-dashboard.${step}.btn-${name}` as const,
      dialog: (step: string, name: string) => `ops-dashboard.${step}.dialog-${name}` as const,
      badge: (step: string, name: string) => `ops-dashboard.${step}.badge-${name}` as const,
    },
    packageSelection: {
      field: (name: string) => `ops-dashboard.package-selection.input-${name}` as const,
      select: (name: string) => `ops-dashboard.package-selection.select-${name}` as const,
      action: (name: string) => `ops-dashboard.package-selection.btn-${name}` as const,
    },
    // Cross-feature dashboard chrome: shared components, utils and the app shell.
    shared: {
      action: (name: string) => `ops-dashboard.shared.btn-${name}` as const,
      field: (name: string) => `ops-dashboard.shared.input-${name}` as const,
      select: (name: string) => `ops-dashboard.shared.select-${name}` as const,
      toggle: (name: string) => `ops-dashboard.shared.toggle-${name}` as const,
      tab: (key: string) => `ops-dashboard.shared.tab-${key}` as const,
      item: (id: number | string) => `ops-dashboard.shared.row-${id}` as const,
    },
    documentPreview: {
      prev: 'ops-dashboard.document-preview.btn-prev',
      next: 'ops-dashboard.document-preview.btn-next',
      zoomIn: 'ops-dashboard.document-preview.btn-zoom-in',
      zoomOut: 'ops-dashboard.document-preview.btn-zoom-out',
      reset: 'ops-dashboard.document-preview.btn-reset-view',
      downloadFallback: 'ops-dashboard.document-preview.btn-download-fallback',
    },
    layout: {
      tab: (key: string) => `ops-dashboard.layout.tab-${key}` as const,
      navLink: (key: string) => `ops-dashboard.layout.link-${key}` as const,
      action: (name: string) => `ops-dashboard.layout.btn-${name}` as const,
      /** Header ⋮ overflow menu entries — items and submenu triggers, keyed by the action they open. */
      menuItem: (name: string) => `ops-dashboard.layout.menu-${name}` as const,
      select: (name: string) => `ops-dashboard.layout.select-${name}` as const,
      toggle: (name: string) => `ops-dashboard.layout.toggle-${name}` as const,
      field: (name: string) => `ops-dashboard.layout.input-${name}` as const,
      /** Read-only status chips in the header (e.g. the Loan Provided tag). */
      badge: (name: string) => `ops-dashboard.layout.badge-${name}` as const,
      /** Read-only values on the sidebar / request cards (e.g. the debit-note line). */
      readonly: (name: string) => `ops-dashboard.layout.readonly-${name}` as const,
    },
  },

  pagination: {
    first: (prefix: string) => `${prefix}.btn-first-page` as const,
    prev: (prefix: string) => `${prefix}.btn-prev-page` as const,
    next: (prefix: string) => `${prefix}.btn-next-page` as const,
    last: (prefix: string) => `${prefix}.btn-last-page` as const,
    pageInput: (prefix: string) => `${prefix}.input-page` as const,
    pageSize: (prefix: string) => `${prefix}.select-page-size` as const,
  },
} as const;

type TestIdNode = string | ((...args: never[]) => string) | { [key: string]: TestIdNode };

export function getAllTestIds(): string[] {
  const out: string[] = [];
  const walk = (node: TestIdNode): void => {
    if (typeof node === 'string') {
      out.push(node);
    } else if (typeof node === 'object' && node !== null) {
      Object.values(node).forEach(walk);
    }
    // functions are skipped — dynamic ids are not enumerable
  };
  walk(testIds);
  return out;
}
